import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  NgZone,
  OnDestroy,
  OnInit,
  AfterViewInit,
  ViewChild,
  HostListener,
  PLATFORM_ID, Inject
} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {PropertyService} from '../services/property.service';
import {CommonModule, formatDate, isPlatformBrowser} from '@angular/common';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {NzIconModule} from 'ng-zorro-antd/icon';
import {NzListModule} from 'ng-zorro-antd/list';
import {CompanyQuickLink} from '../company/company.models';
import {NzResultModule} from 'ng-zorro-antd/result';
import {NzButtonModule} from 'ng-zorro-antd/button';
import {NzSpinModule} from 'ng-zorro-antd/spin';
import {FormsModule} from '@angular/forms';
import {TranslationService} from '../services/translation.service';
import {DOMAIN} from '../constants/app.constants';
import {NzImage, NzImageModule, NzImageService} from 'ng-zorro-antd/image';
import {NzCollapseModule} from 'ng-zorro-antd/collapse';
import {GermanPhoneNormalizerService} from '../services/german.phone.normalizer.service';
import {NzTagModule} from 'ng-zorro-antd/tag';
import {AnnouncementService} from '../services/announcement.service';
import {PushCapabilityState, PushNotificationService} from '../services/push-notification.service';
import {forkJoin, from, mergeMap, of, Subscription, toArray} from 'rxjs';
import {catchError, map} from 'rxjs/operators';
import {DomSanitizer, SafeResourceUrl} from '@angular/platform-browser';
import {NzRateModule} from 'ng-zorro-antd/rate';
import {
  DocumentSection,
  DocumentSectionFile,
  DocumentSectionsComponent,
  isDocumentSectionImageFile,
  openDocumentSectionFile
} from './document-sections/document-sections.component';
import {HospitalityLinkPreviewComponent} from '../hospitality-link-preview/hospitality-link-preview.component';
import {HospitalityChatWidgetComponent} from './hospitality-chat-widget.component';
import {
  TechnicalServiceCategoryOrder,
  TechnicalServiceCategoryKey,
  TechnicalServiceTypeOrder,
  createDefaultTechnicalServiceCategoryOrder,
  flattenTechnicalServiceOrder,
  getCategoryForServiceType,
  normalizeTechnicalServiceCategoryKey,
  normalizeTechnicalServiceCategoryOrder,
  normalizeTechnicalServiceOrder
} from '../utils/technical-service-categories';
import { LandingPageHospitalityMixin, HospitalityLocalTipsViewModel } from './landing-page.hospitality.mixin';
import {NzInputModule} from 'ng-zorro-antd/input';
import {NzDrawerModule} from 'ng-zorro-antd/drawer';
import {NzBadgeModule} from 'ng-zorro-antd/badge';
import {NzToolTipModule} from 'ng-zorro-antd/tooltip';
import {LandingPreviewOverlayContainer} from "./landing-preview-overlay-container";
import { Overlay, OverlayContainer, OverlayRef } from "@angular/cdk/overlay";
import { ComponentPortal } from '@angular/cdk/portal';
import { LandingIosVideoOverlayComponent } from './landing-ios-video-overlay.component';
import { normalizeServiceContact, normalizeServiceTypes } from '../utils/service-type.utils';
import { getScanVisitorId } from '../utils/scan-visitor-identity';
type QuickLinkWithTranslation = CompanyQuickLink & { translatedLabel?: string };
type SectionDef = { id: string; icon: string; labelKey: string; visible: () => boolean };
interface CaretakerContactPersonView {
  _id: string;
  name: string;
  position: string;
  phone: string;
  mobile: string;
  email: string;
  isPrimary: boolean;
  index: number;
}

// Backend weekday keys -> stable order index (Mon=0 ... Sun=6)
const WEEKDAY_INDEX_BY_LOWER: Record<string, number> = {
  monday: 0,
  tuesday: 1,
  wednesday: 2,
  thursday: 3,
  friday: 4,
  saturday: 5,
  sunday: 6,
};

const serviceTypeTranslationKeys: { [key: string]: string } = {
  'Heating': 'HEATING',
  'Sanitary': 'SANITARY',
  'Smoke Detector': 'SMOKE_DETECTOR',
  'Elevator': 'ELEVATOR',
  'Winter Service': 'WINTER_SERVICE',
  'Electrical': 'ELECTRICAL',
  'Garage Door': 'GARAGE_DOOR',
  'Pipe Blockage': 'PIPE_BLOCKAGE',
  'Pipe Burst & Water Damage': 'PIPE_BURST_WATER_DAMAGE',
  'Cable Television': 'CABLE_TELEVISION',
  'Stairwell Cleaning': 'STAIRWELL_CLEANING',
  'Locksmith Service': 'LOCKSMITH_SERVICE',
  'Chimney Sweep': 'CHIMNEY_SWEEP',
  'Meter Reading Service': 'METER_READING_SERVICE',
  'Bulk Waste Collection': 'BULK_WASTE_COLLECTION',
  'Gas Supply/Emergency Service': 'GAS_SUPPLY_EMERGENCY_SERVICE',
  'Power Supply/Emergency Service': 'POWER_SUPPLY_EMERGENCY_SERVICE',
  'Water Supply': 'WATER_SUPPLY',
  'Police': 'POLICE',
  'Garden Maintenance': 'GARDEN_MAINTENANCE',
  'Pest Control': 'PEST_CONTROL',
  'Energy Supply': 'ENERGY_SUPPLY',
  'Waste Disposal': 'WASTE_DISPOSAL',
  'Building Insurance': 'BUILDING_INSURANCE',
  'Fiber / Internet': 'FIBER_INTERNET',
  'Gas Emergency/Gas Odor': 'GAS_EMERGENCY_GAS_ODOR',
  'Recyclable Waste Disposal': 'RECYCLABLE_WASTE_DISPOSAL',
  'Heating Oil Supply': 'HEATING_OIL_SUPPLY',
  'District Heating Supplier': 'DISTRICT_HEATING_SUPPLIER',
  'Lifting Systems and Pumps': 'LIFTING_SYSTEMS_AND_PUMPS',
  'Electric Storage Heater': 'ELECTRIC_STORAGE_HEATER',
  'Locking System Maintenance': 'LOCKING_SYSTEM_MAINTENANCE',
  'Green Roof Maintenance': 'GREEN_ROOF_MAINTENANCE',
  'Rental Mat Service': 'RENTAL_MAT_SERVICE',
  'Local Heating Supplier': 'LOCAL_HEATING_SUPPLIER',
  'Street and Sidewalk Cleaning': 'STREET_AND_SIDEWALK_CLEANING',
  'Double Parker': 'DOUBLE_PARKER',
  'Gate/Gate Systems': 'GATE_GATE_SYSTEMS',
  'Sewer/Blockage': 'SEWER_BLOCKAGE',
  'Solar System/PVA': 'SOLAR_SYSTEM_PVA',
  'Ventilation': 'VENTILATION',
  'Apartment Transfer Station (DHW/Heating)': 'APARTMENT_TRANSFER_STATION_DHW_HEATING',
  'Smoke and Heat Extraction System': 'SMOKE_AND_HEAT_EXTRACTION_SYSTEM',
  'Fire Extinguisher': 'FIRE_EXTINGUISHER',
  'Fire Doors': 'FIRE_DOORS',
  'Fire Alarm System': 'FIRE_ALARM_SYSTEM',
  'Heating Cost Allocator': 'HEATING_COST_ALLOCATOR',
  'WE Meter': 'WE_METER',
  'Coating (UG)': 'COATING_UG',
  'Windows': 'WINDOWS',
  'Roller Shutter/Raffstore': 'ROLLER_SHUTTER_RAFFSTORE',
  'Doors': 'DOORS',
  'Roofer': 'ROOFER',
  'Property Developer': 'PROPERTY_DEVELOPER',
  'Green Pruning': 'GREEN_PRUNING',
};

interface LandingAnnouncement {
  id: string;
  title: string;
  summary: string;
  urgency: 'normal' | 'urgent';
  updatedAt: string;
  validFrom?: string;
  validTo?: string;
  attachments?: DocumentSectionFile[];
  translatedTitle: string;
  translatedSummary: string;
  isNew: boolean;
}
interface EmergencyInstructionVM {
  id: string;
  order?: number;

  category: string;
  title: string;
  who?: string | null;
  how?: string | null;

  showPhoneNumber: boolean;
  phone?: string;

  version?: 'v2';
  scenarioKey?: string;
  sourceLanguage?: 'de' | 'en';
  iconKey?: string | null;
  urgency?: 'normal' | 'important' | 'critical';
  summary?: string | null;
  steps?: EmergencyInstructionStepVM[];

  language?: string;

  translatedTitle?: string;
  translatedWho?: string;
  translatedHow?: string;
  translatedSummary?: string;
}

interface EmergencyInstructionStepActionVM {
  type: 'phone' | 'email' | 'link' | 'document';
  label?: string | null;
  phone?: string | null;
  email?: string | null;
  url?: string | null;
  document?: {
    fileName?: string;
    fileType?: string;
    url?: string;
    s3Key?: string;
    sourceS3Key?: string;
    uploadedAt?: string;
  } | null;
  openInNewTab?: boolean;
  translatedLabel?: string;
}

interface EmergencyInstructionStepVM {
  id: string;
  order: number;
  title: string;
  description?: string | null;
  iconKey?: string | null;
  urgency?: 'normal' | 'important' | 'critical';
  action?: EmergencyInstructionStepActionVM | null;
  translatedTitle?: string;
  translatedDescription?: string;
}

interface AdditionalEmergencyHotline {
  key: 'poison-info-center' | 'veterinary-emergency' | 'telephone-counseling' | 'children-youth' | 'women-girls-support';
  number: string;
  labelKey: string;
  icon: string;
}

@Component({
  selector: 'app-landing-page',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    NzIconModule,
    NzListModule,
    NzResultModule,
    NzButtonModule,
    NzSpinModule,
    FormsModule,
    NzImageModule,
    NzCollapseModule,
    NzTagModule,
    NzRateModule,
    DocumentSectionsComponent,
    HospitalityLinkPreviewComponent,
    HospitalityChatWidgetComponent,
    NzInputModule,
    NzBadgeModule,
    NzDrawerModule,
    NzToolTipModule
  ],
  providers: [
    // ✅ force a landing-page-scoped instance so preview() uses OUR Overlay/OverlayContainer
    NzImageService,

    { provide: OverlayContainer, useClass: LandingPreviewOverlayContainer },
    Overlay,
  ],
  templateUrl: './landing-page.component.html',
  styleUrl: './landing-page.component.less'
})
export class LandingPageComponent extends LandingPageHospitalityMixin implements OnInit, AfterViewInit, OnDestroy {
  private readonly IOS_INSTALL_VIMEO_VIDEO_ID = '1173731279';

  defaultLogoUrl = `${DOMAIN}/assets/scanafy_company_logo_croped.png`;
  iosInstallVideoUrl?: SafeResourceUrl;
  iosInstallVideoModalVisible = false;
  private iosInstallVideoOverlayRef?: OverlayRef;

  translatedCaretakerText: string = '';
  translatedPublicLandingPageMessage: string = '';
  translatedTechnicalServiceText: string = '';

  appMode: 'emergency' | 'hospitality' = 'emergency';

  selectedLanguage: string = 'de';
  languages = [
    { value: 'de', label: 'Deutsch',   flag: 'de' },
    { value: 'en', label: 'English',   flag: 'gb' },
    { value: 'uk', label: 'Українська',flag: 'ua' },
    { value: 'tr', label: 'Türkçe',    flag: 'tr' },
    { value: 'ar', label: 'العربية',   flag: 'sa' },
    { value: 'ru', label: 'Русский',   flag: 'ru' },
    { value: 'pl', label: 'Polski',    flag: 'pl' },
    { value: 'fr', label: 'Français',  flag: 'fr' }
  ];

  // Map language code -> flag code (4x3 svg filenames)
  private flagByLang: Record<string, string> = {
    de: 'de',
    en: 'gb',
    uk: 'ua',
    tr: 'tr',
    ar: 'sa',
    ru: 'ru',
    pl: 'pl',
    fr: 'fr'
  };
  private railKey = 'landing-rail-collapsed';
  showBottomNav = false;
  showSectionRail = false;

  // Desktop/tablet: show the landing page inside a phone-sized preview
  showMobilePreview = false;

  private syncOverlayHost(): void {
    // In real mobile (no preview frame), always use <body>
    if (!this.showMobilePreview) {
      this.overlayTarget = 'body';
    }

    const host =
      this.showMobilePreview && this.overlayTarget === 'preview'
        ? (this.previewRoot?.nativeElement ?? null)
        : null;

    const oc = this.overlayContainer as LandingPreviewOverlayContainer;

    oc.setHost(host);
    oc.updateHostOffsetVars();
  }

  private usePreviewOverlays(): void {
    this.overlayTarget = 'preview';
    this.syncOverlayHost();
  }

  private useBodyOverlays(): void {
    this.overlayTarget = 'body';
    this.syncOverlayHost();
  }

// RTL helper for the banner (Arabic)
  get isRtlLanguage(): boolean {
    return this.selectedLanguage === 'ar';
  }

  railCollapsed = false;
  sectionMenu: SectionDef[] = [];
  bottomNav: SectionDef[] = [];
  activeSectionId?: string;

  // Mobile tabbed navigation (Home / Info / Map)
  mobileActiveTab: 'home' | 'info' | 'map' = 'home';
  mobileInfoSectionId?: string;
  infoSectionMenu: SectionDef[] = [];

  // ===== Emergency initial tab behavior =====
  private initialEmergencyTabApplied = false;

  // Toggle for the old “nudge user to click dashboard” behavior (pulse/toast/hint)
  // For now: OFF.
  private readonly emergencyNudgesEnabled = false;

  // ===== Emergency Instructions tracking (minimal) =====
  private emergencyInstructionTrackingArmed = false;

  private armEmergencyInstructionTracking(): void {
    // avoids tracking the default open (i === 0) or any open caused by re-render
    this.emergencyInstructionTrackingArmed = false;
    setTimeout(() => {
      this.emergencyInstructionTrackingArmed = true;
    }, 0);
  }

  onEmergencyInstructionActiveChange(active: boolean, item: EmergencyInstructionVM, index: number): void {
    if (!active) return;
    if (!this.emergencyInstructionTrackingArmed) return;

    this.track('emergency-instruction-view', {
      id: item.id,
      category: item.category,
      index,
      title: (item.translatedTitle || item.title || '').toString().trim() || null,
      language: this.selectedLanguage
    });
  }

  // prevent the rail highlight from jumping while we scroll programmatically
  private isNavScrolling = false;
  private navScrollTimeoutId: any;
  private lastNavClickAt = 0;

  getFlagFor(value: string): string {
    return this.flagByLang[value] || 'de';
  }

  // Expose the currently selected flag to the template
  get selectedFlag(): string {
    return this.getFlagFor(this.selectedLanguage);
  }

  getSelectedLanguage(): any {
    return this.languages.find(lang => lang.value === this.selectedLanguage) || this.languages[0];
  }

  getSelectedLanguageFlag(): string {
    return this.getSelectedLanguage()?.flag || 'de';
  }

  getSelectedLanguageLabel(): string {
    return this.getSelectedLanguage()?.label || this.selectedLanguage;
  }

  toggleLanguageDropdown(event?: Event): void {
    event?.stopPropagation();
    this.langDropdownOpen = !this.langDropdownOpen;
  }

  selectLandingLanguage(language: string, event?: Event): void {
    event?.stopPropagation();

    if (this.selectedLanguage !== language) {
      this.changeLanguage(language);
    }

    this.langDropdownOpen = false;
  }

  companyId: string | undefined;
  propertyId: string | undefined;
  company: any;
  property: any;
  caretaker: any;
  caretakerContactPersons: CaretakerContactPersonView[] = [];
  translatedCaretakerNote = '';
  propertyManagers: any[] | undefined;
  technicalAssetsManagers: any[] | undefined;
  sevManagers: any[] | undefined;
  accountants: any[] | undefined;
  howToReachUsManagers: any[] = [];
  technicalServices: any[] | undefined;
  technicalServiceGroups: Array<{ key: TechnicalServiceCategoryKey; services: any[] }> = [];
  emergencyTechnicalServices: any[] = [];
  technicalServiceCategoryOrder: TechnicalServiceCategoryOrder = createDefaultTechnicalServiceCategoryOrder();
  selectedThemeColor = '#1890ff'; // Default theme color
  formattedAvailability: any[] = [];
  formattedEmergencyAvailability: any[] = [];
  showAvailability: boolean = false;
  showEmergencyAvailability: boolean = false;
  showEmergencyNumber112: boolean = false;
  showEmergencyNumber110: boolean = false;
  showEmergencyNumber116117: boolean = false;
  showPoisonInfoCenterHotline: boolean = false;
  poisonInfoCenterHotlineNumber: string = '';
  showVeterinaryEmergencyHotline: boolean = false;
  veterinaryEmergencyHotlineNumber: string = '';
  showTelephoneCounselingHotline: boolean = false;
  showChildrenYouthHotline: boolean = false;
  showWomenGirlsEmergencyHotline: boolean = false;
  additionalEmergencyHotlines: AdditionalEmergencyHotline[] = [];
  hasVisibleEmergencyNumbers: boolean = false;
  quickLinks: QuickLinkWithTranslation[] = [];
  caretakerFormattedAvailability: any[] = [];

  hasError: boolean = false;
  nzErrorStatus: '403' | '404' | '500' | 'success' | 'error' | 'info' | 'warning' = 'info';
  errorTitle: string = '';
  errorSubtitle: string = '';

  loading: boolean = true;

  announcements: LandingAnnouncement[] = [];
  private rawAnnouncements: any[] = [];
  announcementsLoading = true;
  private translationRunId = 0;
  lastAnnouncementUpdated?: Date;
  private lastSeenAnnouncementsAt = 0;
  private announcementSeenStorageKey?: string;
  langDropdownOpen = false;
  private overlayTarget: 'preview' | 'body' = 'preview';
  showAnnouncements = false;
  // Drawer visibility for announcement center
  notificationsDrawerOpen = false;

  // ===== Emergency Instructions (public, landing-page) =====
  emergencyInstructions: EmergencyInstructionVM[] = [];
  private rawEmergencyInstructions: EmergencyInstructionVM[] = [];
  emergencyInstructionsLoading = false;
  emergencyInstructionsError = false;

  pushState?: PushCapabilityState;
  showPushCta = false;
  showPushOptOut = false;
  showIosInstallTip = false;
  private iosTipDismissedKey?: string;
  private pushStateSubscription?: Subscription;
  private pushCtaDismissed = false;
  private iosTipDismissed = false;
  optOutProcessing = false;
  private pushStateLogged = false;

  documentSections: DocumentSection[] = [];
  loadingDocs = false;
  @ViewChild('localTipsMapContainer') private localTipsMapContainer?: ElementRef<HTMLDivElement>;
  @ViewChild(HospitalityChatWidgetComponent) private hospitalityChatWidget?: HospitalityChatWidgetComponent;
  @ViewChild('previewRoot', { read: ElementRef }) private previewRoot?: ElementRef<HTMLElement>;
  private localTipsMap?: any;
  private localTipsMapMarkers: any[] = [];
  private localTipsTileLayer?: any;
  private localTipsMapFrameRequested = false;
  private leafletModule?: typeof import('leaflet');

  infoTabHintDismissed = false;
  private infoTabHintKey?: string;

  // ===== Emergency nudges (Dashboard tab attention + scroll-toast) =====
  showDashboardAttention = false;
  showDashboardToast = false;

  private dashboardAttentionTimeoutId: any;
  private dashboardToastTimeoutId: any;

  private dashboardPulseSeenKey?: string;
  private dashboardToastSeenKey?: string;
  private announcementsNudgeSeenKey?: string;
  private announcementsPopupSeenKey?: string;

  // Touch gesture nudge (when Home has no real scroll)
  private touchStartY: number | null = null;
  isLogoTall = false;
  isLogoWide = false;

  isEmergencyLogoTall = false;
  isEmergencyLogoWide = false;

  private readonly EMERGENCY_CATEGORY_ICON: Record<string, string> = {
    key_access: 'key',
    main_shut_off: 'poweroff',
    emergency_tool_supply: 'tool',
    special_building_rule: 'warning',
    other: 'question-circle',
  };

  emergencyInstructionIconType(category?: string): string {
    const key = (category || 'other').toLowerCase();
    return this.EMERGENCY_CATEGORY_ICON[key] || 'info';
  }

  emergencyInstructionGuideIconType(item: EmergencyInstructionVM): string {
    if (item?.iconKey) {
      return this.resolveEmergencyInstructionIconKey(item.iconKey);
    }
    return this.emergencyInstructionIconType(item?.category);
  }

  resolveEmergencyInstructionIconKey(iconKey?: string | null): string {
    const normalized = (iconKey || '').trim().toLowerCase();
    switch (normalized) {
      case 'fire':
        return 'fire';
      case 'alert':
        return 'alert';
      case 'tool':
        return 'tool';
      case 'phone':
        return 'phone';
      case 'notification':
        return 'notification';
      case 'home':
        return 'home';
      case 'safety':
        return 'safety-certificate';
      case 'file':
        return 'file-text';
      case 'team':
        return 'team';
      case 'environment':
        return 'environment';
      case 'thunderbolt':
        return 'thunderbolt';
      case 'key':
        return 'key';
      case 'lock':
        return 'lock';
      case 'unlock':
        return 'unlock';
      case 'gateway':
      case 'exit':
        return 'gateway';
      case 'build':
      case 'building':
        return 'build';
      case 'shop':
      case 'basement':
        return 'shop';
      case 'pushpin':
      case 'valve':
        return 'pushpin';
      case 'camera':
        return 'camera';
      case 'security-scan':
      case 'security':
        return 'security-scan';
      case 'customer-service':
      case 'help':
        return 'customer-service';
      case 'info-circle':
      case 'info':
        return 'info-circle';
      case 'compass':
      case 'map':
        return 'compass';
      case 'warning':
      default:
        return normalized || 'warning';
    }
  }

  isEmergencyInstructionV2(item: EmergencyInstructionVM): boolean {
    return Array.isArray(item?.steps) && item.steps.length > 0;
  }

  emergencyInstructionUrgencyLabelKey(urgency?: string | null): string {
    switch (urgency) {
      case 'critical':
        return 'PROPERTY_INSTRUCTIONS.URGENCY.CRITICAL';
      case 'important':
        return 'PROPERTY_INSTRUCTIONS.URGENCY.IMPORTANT';
      default:
        return 'PROPERTY_INSTRUCTIONS.URGENCY.NORMAL';
    }
  }

  emergencyInstructionUrgencyColor(urgency?: string | null): string {
    switch (urgency) {
      case 'critical':
        return 'red';
      case 'important':
        return 'orange';
      default:
        return 'blue';
    }
  }

  trackEmergencyInstructionAction(item: EmergencyInstructionVM, step: EmergencyInstructionStepVM): void {
    if (!step?.action) {
      return;
    }

    const title = item.translatedTitle || item.title;
    const stepTitle = step.translatedTitle || step.title;

    if (step.action.type === 'phone' && step.action.phone) {
      this.trackContactClick('phone', step.action.phone, 'emergency-instruction', `${title} · ${stepTitle}`);
      return;
    }

    if (step.action.type === 'email' && step.action.email) {
      this.trackContactClick('email', step.action.email, 'emergency-instruction', `${title} · ${stepTitle}`);
      return;
    }

    if (step.action.type === 'link' && step.action.url) {
      this.track('link-click', {
        label: step.action.translatedLabel || step.action.label || stepTitle,
        url: step.action.url,
        relatedTo: 'emergency-instruction',
        relatedName: title,
      });
      return;
    }

    if (step.action.type === 'document') {
      this.trackDocumentView(title || this.translate.instant('LANDING.EMERGENCY_INSTRUCTIONS.TITLE'), step.action.document?.fileName || stepTitle);
    }
  }

  private getEmergencyInstructionDocumentFile(action?: EmergencyInstructionStepActionVM | null): DocumentSectionFile | null {
    const snapshotS3Key = action?.document?.s3Key?.trim() || '';
    const snapshotSourceS3Key = action?.document?.sourceS3Key?.trim() || '';
    const snapshotUrl = action?.document?.url?.trim() || action?.url?.trim() || '';
    const snapshotFileName = action?.document?.fileName?.trim() || action?.translatedLabel || action?.label || 'document';
    const snapshotFileType = action?.document?.fileType?.trim() || '';

    const liveFile = this.documentSections
      .flatMap((section) => section.files || [])
      .find((file) => {
        const liveS3Key = file?.s3Key?.trim() || '';
        const liveSourceS3Key = file?.sourceS3Key?.trim() || '';
        const liveUrl = file?.url?.trim() || '';
        const liveFileName = file?.fileName?.trim() || '';
        const liveFileType = file?.fileType?.trim().toLowerCase() || '';

        if (snapshotS3Key && liveS3Key && snapshotS3Key === liveS3Key) {
          return true;
        }
        if (snapshotSourceS3Key && liveSourceS3Key && snapshotSourceS3Key === liveSourceS3Key) {
          return true;
        }
        if (snapshotS3Key && liveSourceS3Key && snapshotS3Key === liveSourceS3Key) {
          return true;
        }
        if (snapshotSourceS3Key && liveS3Key && snapshotSourceS3Key === liveS3Key) {
          return true;
        }
        if (snapshotUrl && liveUrl && snapshotUrl === liveUrl) {
          return true;
        }
        if (snapshotFileName && liveFileName && snapshotFileName === liveFileName) {
          if (!snapshotFileType || !liveFileType) {
            return true;
          }
          return snapshotFileType.toLowerCase() === liveFileType;
        }
        return false;
      });

    if (liveFile?.url?.trim()) {
      return {
        fileName: liveFile.fileName,
        fileType: liveFile.fileType || '',
        url: liveFile.url,
        s3Key: liveFile.s3Key,
        sourceS3Key: liveFile.sourceS3Key,
      };
    }

    if (!snapshotUrl) {
      return null;
    }

    return {
      fileName: snapshotFileName,
      fileType: snapshotFileType,
      url: snapshotUrl,
      s3Key: snapshotS3Key || undefined,
      sourceS3Key: snapshotSourceS3Key || undefined,
    };
  }

  getEmergencyInstructionDocumentUrl(action?: EmergencyInstructionStepActionVM | null): string | null {
    return this.getEmergencyInstructionDocumentFile(action)?.url || null;
  }

  onEmergencyInstructionDocumentActionClick(
    event: Event,
    item: EmergencyInstructionVM,
    step: EmergencyInstructionStepVM,
    action: EmergencyInstructionStepActionVM,
  ): void {
    const file = this.getEmergencyInstructionDocumentFile(action);
    if (!file?.url) {
      return;
    }

    event.preventDefault();
    this.trackEmergencyInstructionAction(item, step);
    openDocumentSectionFile(file, (url) => this.openImagePreview(url));
  }

  isEmergencyInstructionDocumentImage(action?: EmergencyInstructionStepActionVM | null): boolean {
    return isDocumentSectionImageFile(this.getEmergencyInstructionDocumentFile(action));
  }

  private syncEmergencyHotlineState(): void {
    const configuredHotlines: Array<AdditionalEmergencyHotline & { enabled: boolean }> = [
      {
        key: 'poison-info-center',
        enabled: this.showPoisonInfoCenterHotline && !!this.poisonInfoCenterHotlineNumber.trim(),
        number: this.poisonInfoCenterHotlineNumber.trim(),
        labelKey: 'COMPANY_PAGE.EMERGENCY_NUMBER_POISON_INFO_CENTER_LABEL',
        icon: 'experiment',
      },
      {
        key: 'veterinary-emergency',
        enabled: this.showVeterinaryEmergencyHotline && !!this.veterinaryEmergencyHotlineNumber.trim(),
        number: this.veterinaryEmergencyHotlineNumber.trim(),
        labelKey: 'COMPANY_PAGE.EMERGENCY_NUMBER_VETERINARY_EMERGENCY_HOTLINE_LABEL',
        icon: 'heart',
      },
      {
        key: 'telephone-counseling',
        enabled: this.showTelephoneCounselingHotline,
        number: '116 123',
        labelKey: 'COMPANY_PAGE.EMERGENCY_NUMBER_TELEPHONE_COUNSELING_LABEL',
        icon: 'customer-service',
      },
      {
        key: 'children-youth',
        enabled: this.showChildrenYouthHotline,
        number: '116 111',
        labelKey: 'COMPANY_PAGE.EMERGENCY_NUMBER_CHILDREN_YOUTH_LABEL',
        icon: 'team',
      },
      {
        key: 'women-girls-support',
        enabled: this.showWomenGirlsEmergencyHotline,
        number: '116 016',
        labelKey: 'COMPANY_PAGE.EMERGENCY_NUMBER_WOMEN_GIRLS_SUPPORT_LABEL',
        icon: 'alert',
      },
    ];

    this.additionalEmergencyHotlines = configuredHotlines
      .filter((hotline) => hotline.enabled)
      .map(({ enabled: _enabled, ...hotline }) => hotline);

    this.hasVisibleEmergencyNumbers = Boolean(
      this.showEmergencyNumber112
      || this.showEmergencyNumber110
      || this.showEmergencyNumber116117
      || this.additionalEmergencyHotlines.length
      || this.emergencyTechnicalServices.length
    );
  }

  getEmergencyHotlineDialNumber(number: string): string {
    return String(number || '').replace(/\s+/g, '');
  }

  trackEmergencyHotline(_index: number, hotline: AdditionalEmergencyHotline): string {
    return hotline.key;
  }

  constructor(
    private route: ActivatedRoute,
    propertyService: PropertyService,
    translate: TranslateService,
    translationService: TranslationService,
    cdr: ChangeDetectorRef,
    private imageService: NzImageService,
    private dePhone: GermanPhoneNormalizerService,
    private announcementService: AnnouncementService,
    private pushService: PushNotificationService,
    sanitizer: DomSanitizer,
    private ngZone: NgZone,
    private overlay: Overlay,
    private overlayContainer: OverlayContainer,
    @Inject(PLATFORM_ID) private platformId: Object,
  ) {
    super(translate, translationService, propertyService, cdr, sanitizer);
    const stored = this.getLocalStorageItem('selectedLandingPageLanguage');
    this.selectedLanguage = stored || this.detectBrowserLanguage() || 'de';
  }

  private getLocalStorageItem(key: string): string | null {
    try {
      return localStorage.getItem(key);
    } catch (_error) {
      return null;
    }
  }

  private detectBrowserLanguage(): string | null {
    const browserLanguages = navigator.languages?.length
      ? navigator.languages
      : [navigator.language];

    for (const browserLang of browserLanguages) {
      const langCode = browserLang?.split('-')[0];

      if (this.languages.some(l => l.value === langCode)) {
        return langCode;
      }
    }

    return null;
  }

  ngOnInit(): void {
    this.changeLanguage(this.selectedLanguage);
    this.loading = true;

    if (isPlatformBrowser(this.platformId)) {
      this.updateResponsiveFlags();
    }

    // Get companyId and propertyId from URL
    this.companyId = this.route.snapshot.paramMap.get('companyId')!;
    this.propertyId = this.route.snapshot.paramMap.get('propertyId')!;

    // ✅ Frontend validation: if IDs are invalid, show "Invalid request" page
    if (!this.isValidObjectId(this.companyId) || !this.isValidObjectId(this.propertyId)) {
      this.loading = false;
      this.hasError = true;
      this.nzErrorStatus = 'warning'; // will map to INVALID_REQUEST messages
      this.updateErrorMessages();
      return; // ⬅ stop here, do not call any HTTP APIs
    }

    this.translationService.setLandingContext({
      companyId: this.companyId,
      propertyId: this.propertyId,
    });
    this.setupStorageKeys();
    this.loadAnnouncements();
    this.initializePush();

    // Fetch property data based on companyId and propertyId
    const visitorId = getScanVisitorId(this.companyId, this.propertyId);
    this.propertyService.getPropertyDetails(this.companyId, this.propertyId, visitorId).subscribe(
      (data) => {
        this.property = data.property;
        this.company = data.company;
        this.appMode = this.company?.appMode === 'hospitality' ? 'hospitality' : 'emergency';
        this.hospitalityDetailsRaw = this.property?.hospitalityDetails || {};
        this.hospitalityLandingRaw = this.company?.hospitalityLanding || {};
        this.hospitalityRatingSummary =
          this.normalizeHospitalityRatingSummary(this.property?.hospitalityRatingSummary) ||
          this.normalizeHospitalityRatingSummary(this.hospitalityDetailsRaw?.ratingSummary) ||
          this.normalizeHospitalityRatingSummary(this.hospitalityLandingRaw?.ratingSummary);
        this.caretaker = data.caretaker;
        this.caretakerContactPersons = this.normalizeCaretakerContactPersons(this.caretaker);
        this.propertyManagers = data.propertyManagers;
        this.technicalAssetsManagers = data.technicalAssetsManagers;
        this.sevManagers = data.sevManagers;
        this.accountants = data.accountants;
        this.howToReachUsManagers = this.computeHowToReachUsManagers();
        const serviceOrder = normalizeTechnicalServiceOrder(this.company?.technicalServiceTypeOrder);
        const categoryOrder = normalizeTechnicalServiceCategoryOrder(this.company?.technicalServiceCategoryOrder);
        const preparedServices = this.prepareTechnicalServices(data.technicalServices, serviceOrder, categoryOrder);
        const emergencyServiceTypes = this.company?.landingPageEmergencyServicePlacementEnabled === true
          ? new Set(normalizeServiceTypes(this.company?.landingPageEmergencyServiceTypes))
          : new Set<string>();
        this.emergencyTechnicalServices = preparedServices.flat.filter((service) =>
          emergencyServiceTypes.has(service.serviceType)
        );
        this.technicalServiceGroups = preparedServices.groups
          .map((group) => ({
            ...group,
            services: group.services.filter((service) => !emergencyServiceTypes.has(service.serviceType))
          }))
          .filter((group) => group.services.length > 0);
        this.technicalServices = preparedServices.flat.filter(
          (service) => !emergencyServiceTypes.has(service.serviceType)
        );
        this.company.technicalServiceTypeOrder = preparedServices.order;
        this.company.technicalServiceCategoryOrder = preparedServices.categoryOrder;
        this.technicalServiceCategoryOrder = preparedServices.categoryOrder;
        if (this.property) {
          this.property.technicalServices = this.technicalServices;
        }

        if (this.caretaker && this.caretaker?.availability){
          this.caretakerFormattedAvailability = this.formatOfficeHours(this.caretaker?.availability);
        }
        this.translateCaretakerNote();

        // Set the theme color from company data
        this.selectedThemeColor = this.company.themeColor || '#1890ff'; // Fallback to default if themeColor is missing

        this.showAvailability = data.company.showAvailability;
        this.showEmergencyAvailability = data.company.showEmergencyAvailability;
        this.showEmergencyNumber112 = !!data.company.showEmergencyNumber112;
        this.showEmergencyNumber110 = !!data.company.showEmergencyNumber110;
        this.showEmergencyNumber116117 = this.showEmergencyNumber112 || this.showEmergencyNumber110;
        this.showPoisonInfoCenterHotline = !!data.company.showPoisonInfoCenterHotline;
        this.poisonInfoCenterHotlineNumber = String(data.company.poisonInfoCenterHotlineNumber || '').trim();
        this.showVeterinaryEmergencyHotline = !!data.company.showVeterinaryEmergencyHotline;
        this.veterinaryEmergencyHotlineNumber = String(data.company.veterinaryEmergencyHotlineNumber || '').trim();
        this.showTelephoneCounselingHotline = !!data.company.showTelephoneCounselingHotline;
        this.showChildrenYouthHotline = !!data.company.showChildrenYouthHotline;
        this.showWomenGirlsEmergencyHotline = !!data.company.showWomenGirlsEmergencyHotline;
        this.syncEmergencyHotlineState();
        this.quickLinks = Array.isArray(data.company.quickLinks)
          ? data.company.quickLinks
            .filter((link: CompanyQuickLink) => link?.label && link?.url)
            .map((l: CompanyQuickLink) => ({ ...l })) as QuickLinkWithTranslation[]
          : [];

        this.translateQuickLinks();
        if (this.company.showAvailability && this.company.availability){
          this.formattedAvailability = this.formatAvailability(this.company.availability);
        }
        if (this.company.showEmergencyAvailability && this.company.emergencyAvailability){
          this.formattedEmergencyAvailability = this.formatAvailability(this.company.emergencyAvailability);
        }
        this.translateUserText();
        this.translateTechnicalServiceNotes();

        // ✅ Emergency-only: load + translate emergency instructions (public endpoint)
        if (this.appMode === 'emergency') {
          this.loadEmergencyInstructions();
        }
        // 🔒 Only build + translate hospitality sections when in hospitality mode
        if (this.appMode === 'hospitality') {
          this.buildHospitalityViewModels();
        }
        this.buildSectionIndex();
        this.loading = false;

        // ✅ Emergency mode: start on Info tab (mobile only)
        setTimeout(() => {
          this.applyInitialEmergencyMobileTab();
          // (nudges disabled for now)
          if (this.emergencyNudgesEnabled) {
            this.startDashboardAttentionIfNeeded();
          }
          this.cdr.markForCheck();
        }, 0);
        this.loadDocuments();
      },
      (error) => {
        this.loading = false;
        this.hasError = true;

        if (error.status === 403) {
          this.nzErrorStatus = '403';
        } else if (error.status === 404) {
          this.nzErrorStatus = '404';
        } else if (error.status === 400) {
          this.nzErrorStatus = 'warning';
        } else {
          this.nzErrorStatus = '500'; // Fallback for unexpected errors
        }

        this.updateErrorMessages();

        // console.error('Error fetching property data:', error);
      }
    );
    const stored = localStorage.getItem(this.railKey);
    this.railCollapsed = stored ? stored === '1' : true; // default collapsed
  }

  ngAfterViewInit(): void {
    this.scheduleLocalTipsMapUpdate();

    // Defer any DOM-size / scroll-derived state updates to next macrotask
    // to avoid NG0100 on the first check.
    setTimeout(() => {
      this.updateResponsiveFlags();
      this.syncOverlayHost();

      // ✅ ensures initial tab logic runs when mobile flags are known
      this.applyInitialEmergencyMobileTab();

      // (nudges disabled for now)
      if (this.emergencyNudgesEnabled) {
        this.startDashboardAttentionIfNeeded();
      }

      this.updateActiveSection();
      this.cdr.detectChanges();
    }, 0);
  }

  ngOnDestroy(): void {
    this.translationService.setLandingContext(null);
    this.disposeIosInstallVideoOverlay();
    this.pushStateSubscription?.unsubscribe();
    this.ratingSubmissionSub?.unsubscribe();
    this.destroyLocalTipsMap();
    clearTimeout(this.dashboardAttentionTimeoutId);
    clearTimeout(this.dashboardToastTimeoutId);
    (this.overlayContainer as LandingPreviewOverlayContainer).setHost(null);
  }

  openDigitalConcierge(event?: Event): void {
    event?.stopPropagation();
    event?.preventDefault();
    this.hospitalityChatWidget?.openWidget();
  }

  private updateResponsiveFlags(): void {
    const isMobileViewport = window.matchMedia('(max-width: 768px)').matches;

    // Desktop/tablet => show phone preview
    this.showMobilePreview = !isMobileViewport;

    // Treat preview as “mobile-like” so your current UI logic works
    const isMobileLike = isMobileViewport || this.showMobilePreview;

    this.showBottomNav = isMobileLike;
    this.showSectionRail = !isMobileLike;

    // Reset target when switching modes:
    // - entering preview -> default to preview overlays (unless lang dropdown is currently open)
    // - leaving preview -> always body
    if (!this.showMobilePreview) {
      this.overlayTarget = 'body';
    } else if (!this.langDropdownOpen) {
      this.overlayTarget = 'preview';
    }

    this.syncOverlayHost();

    this.cdr.markForCheck();
    this.applyInitialEmergencyMobileTab();
  }

  private applyInitialEmergencyMobileTab(): void {
    // Run once, only after data is loaded, only on mobile, only in emergency mode.
    if (this.initialEmergencyTabApplied) return;
    if (this.loading || this.hasError) return;
    if (!this.showBottomNav) return;
    if (this.appMode !== 'emergency') return;

    this.mobileActiveTab = 'info';
    this.mobileInfoSectionId = undefined;
    this.initialEmergencyTabApplied = true;
    this.cdr.markForCheck();
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.updateResponsiveFlags();
    this.updateActiveSection();
    (this.overlayContainer as any).updateHostOffsetVars?.();
  }

  // ===== Mobile gesture-based nudge: swipe-up attempt on Home (even if no scroll happens) =====
  @HostListener('window:touchstart', ['$event'])
  onTouchStart(e: TouchEvent): void {
    if (!this.emergencyNudgesEnabled) return;
    if (
      this.appMode !== 'emergency' ||
      !this.showBottomNav ||
      this.loading ||
      this.hasError ||
      this.mobileActiveTab !== 'home'
    ) {
      return;
    }

    if (e.touches.length !== 1) return;
    this.touchStartY = e.touches[0].clientY;
  }

  @HostListener('window:touchmove', ['$event'])
  onTouchMove(e: TouchEvent): void {
    if (
      this.appMode !== 'emergency' ||
      !this.showBottomNav ||
      this.loading ||
      this.hasError ||
      this.mobileActiveTab !== 'home'
    ) {
      return;
    }

    if (this.touchStartY == null) return;
    if (e.touches.length !== 1) return;

    const currentY = e.touches[0].clientY;
    const deltaY = currentY - this.touchStartY;

    // deltaY < 0 => swipe up. threshold avoids accidental triggers.
    if (deltaY < -25) {
      // stop pulse on first interaction
      if (this.showDashboardAttention) {
        this.stopDashboardAttention();
      }

      // show toast even if page doesn't scroll
      this.showDashboardToastOnce();

      // one-shot per gesture
      this.touchStartY = null;
    }
  }

  @HostListener('window:touchend')
  onTouchEnd(): void {
    this.touchStartY = null;
  }

  @HostListener('window:scroll')
  onWindowScroll(): void {
    if (this.showMobilePreview) {
      (this.overlayContainer as any).updateHostOffsetVars?.();
    }
    // ===== Mobile emergency behavior-based nudge (Home scroll => toast) =====
    if (this.emergencyNudgesEnabled) {
      if (
        this.showBottomNav &&
        this.appMode === 'emergency' &&
        this.mobileActiveTab === 'home' &&
        !this.loading &&
        !this.hasError
      ) {
        const y = window.scrollY || document.documentElement.scrollTop || 0;

        if (this.showDashboardAttention && y > 0) {
          this.stopDashboardAttention();
        }

        if (y > 80) {
          this.showDashboardToastOnce();
        }
      }
    }
    const now = Date.now();

    // Only care about scroll -> active section when the rail is visible (desktop)
    if (!this.showSectionRail) {
      return;
    }

    // Ignore scroll events that happen shortly after a nav click
    // or while we explicitly marked "nav scrolling".
    const delta = now - this.lastNavClickAt;

    if (this.isNavScrolling || delta < 1200) {
      return;
    }

    this.updateActiveSection();
  }

  @HostListener('document:click')
  onDocumentClick(): void {
    if (this.langDropdownOpen) {
      this.langDropdownOpen = false;
      this.cdr.markForCheck();
    }
  }

  @HostListener('document:keydown.escape')
  onEscapeKey(): void {
    if (this.langDropdownOpen) {
      this.langDropdownOpen = false;
      this.cdr.markForCheck();
    }
  }

  onAnnouncementsIconClick(): void {
    this.markAnnouncementsPopupSeen();
    this.markAnnouncementsNudgeSeen();
    this.usePreviewOverlays(); // ✅ ensure drawer renders inside preview

    // make sure the lang select overlay cannot sit above the drawer
    this.langDropdownOpen = false;
    const active = document.activeElement as HTMLElement | null;
    if (active) { active.blur(); }

    this.notificationsDrawerOpen = true;

    this.markVisibleUnreadAnnouncementsViewed();
  }

  onAnnouncementsDrawerClose(): void {
    this.notificationsDrawerOpen = false;
  }

  // Computed counts for the badge
  get announcementsCount(): number {
    return this.announcements?.length || 0;
  }

  get unreadAnnouncementsCount(): number {
    return (this.announcements || []).filter(a => a.isNew).length;
  }

  get hasAnnouncements(): boolean {
    return this.announcementsCount > 0;
  }

  get hasUrgentAnnouncements(): boolean {
    return (this.announcements || []).some(a => a.urgency === 'urgent');
  }

  private updateActiveSection(): void {
    if (!this.showSectionRail) {
      return;
    }

    const sections = this.sectionMenu;
    let bestId: string | undefined;
    let bestTop = Number.POSITIVE_INFINITY;

    sections.forEach(s => {
      const el = document.getElementById(s.id);
      if (!el) return;

      const rect = el.getBoundingClientRect();
      const offsetTop = Math.abs(rect.top);

      // Prefer the section nearest to the top but not far above
      if (rect.top <= 140 && offsetTop < bestTop) {
        bestTop = offsetTop;
        bestId = s.id;
      }
    });

    if (bestId && bestId !== this.activeSectionId) {
      this.activeSectionId = bestId;
      this.cdr.markForCheck();
    }
  }

  scrollTo(id: string): void {
    // Map logical nav ids to the actual DOM section we want to start at
    let targetId = id;

    // "Caretaker" (Contacts) should always start where the caretaker text begins
    if (id === 'contacts-group') {
      targetId = 'caretaker-text';
    }
    // "Management" (service hours tile) should start at the company info block
    else if (id === 'availability' || id === 'emergency-availability') {
      targetId = 'contact';
    }

    const el = document.getElementById(targetId);
    if (!el) {
      return;
    }

    // keep the logical nav id for highlighting on the desktop rail
    this.activeSectionId = id;
    this.cdr.markForCheck();

    // mark that a nav click just happened
    this.lastNavClickAt = Date.now();
    this.isNavScrolling = true;

    if (this.navScrollTimeoutId) {
      clearTimeout(this.navScrollTimeoutId);
    }

    el.scrollIntoView({ behavior: 'smooth', block: 'start' });

    // after the scroll animation is very likely done, allow scroll updates again
    this.navScrollTimeoutId = setTimeout(() => {
      this.isNavScrolling = false;
    }, 1200);
  }

  toggleRailCollapsed(): void {
    this.railCollapsed = !this.railCollapsed;
    localStorage.setItem(this.railKey, this.railCollapsed ? '1' : '0');
  }

  setMobileTab(tab: 'home' | 'info' | 'map'): void {
    this.mobileActiveTab = tab;

    if (!this.showBottomNav) return;

    // Emergency nudges: stop when leaving Home, mark toast seen when opening Dashboard
    if (this.emergencyNudgesEnabled && this.appMode === 'emergency') {
      if (tab === 'home') {
        this.startDashboardAttentionIfNeeded();
      } else {
        // leaving Home -> don't keep nudges visible
        this.stopDashboardAttention();
        this.dismissDashboardToast(false);
      }

      if (tab === 'info') {
        // if user opened Dashboard, don’t show the scroll toast later
        this.dismissDashboardToast(true);
        this.stopDashboardAttention();
      }
    }

    if (tab === 'home') {
      // Home: clear selection
      this.mobileInfoSectionId = undefined;
      return;
    }

    if (tab === 'map') {
      // Hospitality only – Map shows ONLY Local Tips
      this.mobileInfoSectionId = undefined;
      if (this.appMode === 'hospitality' && this.hospitalityLocalTips) {
        // Ensure the map is rendered, then reflow + refit after it becomes visible
        this.ngZone.runOutsideAngular(() => {
          requestAnimationFrame(() => {
            this.scheduleLocalTipsMapUpdate();
            this.refitLocalTipsMapOnShow();
            this.scrollTo('local-tips');
          });
        });
      }
      return;
    }

    // Info: show dashboard first (no auto-select)
    if (tab === 'info') {
      this.mobileInfoSectionId = undefined;

      // ✅ FIX: Info dashboard removes hospitality-page via *ngIf, so the Leaflet container is destroyed.
      // Drop the map instance so it recreates cleanly when returning to Map.
      if (this.appMode === 'hospitality') {
        this.destroyLocalTipsMap();
      }

      return;
    }
  }

  private getFirstExistingContactId(): string {
    // "Contacts" tile is now "Caretaker" and should open that card only
    if (this.caretaker) {
      return 'caretaker';
    }
    // Safety fallback
    return 'contact';
  }

  onMobileInfoSectionSelect(id: string): void {
    // Emergency mobile hub (Home): open selected section in the "info" view (tabs are hidden)
    if (this.showBottomNav && this.appMode === 'emergency' && this.mobileActiveTab === 'home') {
      this.mobileActiveTab = 'info';
    }

    this.mobileInfoSectionId = id;

    if (this.showBottomNav) {
      const target = (id === 'contacts-group')
        ? this.getFirstExistingContactId()
        : id;
      setTimeout(() => this.scrollTo(target), 0);
    }
  }

  get hasAnyAvailability(): boolean {
    return !!(this.showAvailability && (this.formattedAvailability?.length || 0) > 0);
  }
  get hasAnyEmergencyAvailability(): boolean {
    return !!(this.showEmergencyAvailability && (this.formattedEmergencyAvailability?.length || 0) > 0);
  }

  get hasAnyManagementInfo(): boolean {
    const hasPM =
      (this.propertyManagers?.length || 0) > 0 &&
      !!this.company?.showPropertyManager;

    const hasTAM =
      (this.technicalAssetsManagers?.length || 0) > 0 &&
      !!this.company?.showTechnicalAssetsManager;

    const hasSev =
      (this.sevManagers?.length || 0) > 0 &&
      !!this.company?.showSevManager;

    const hasAccountant =
      (this.accountants?.length || 0) > 0 &&
      !!this.company?.showAccountant;

    return (
      this.hasAnyAvailability ||
      this.hasAnyEmergencyAvailability ||
      hasPM ||
      hasTAM ||
      hasSev ||
      hasAccountant
    );
  }

  get isServiceHoursTileActiveOnMobile(): boolean {
    return this.showBottomNav && this.appMode === 'emergency' &&
      this.mobileActiveTab === 'info' &&
      (this.mobileInfoSectionId === 'availability' || this.mobileInfoSectionId === 'emergency-availability');
  }

  // Visibility for hospitality sections on mobile
  isHospitalitySectionVisible(sectionId: string): boolean {
    // Desktop or non-hospitality → show all
    if (!this.showBottomNav || this.appMode !== 'hospitality') return true;

    // Home tab shows only hero + quick actions in template → hide other sections
    if (this.mobileActiveTab === 'home') return false;

    // Map tab shows ONLY local tips
    if (this.mobileActiveTab === 'map') return sectionId === 'local-tips';

    // Info tab:
    // - Local tips are only on Map, never here
    if (sectionId === 'local-tips') return false;

    // - If no selection yet, we show the dashboard (tiles), not sections
    if (!this.mobileInfoSectionId) return false;

    // - Show only the chosen section
    return this.mobileInfoSectionId === sectionId
      || this.mobileInfoSectionId === this.prefId('docs') && sectionId === this.prefId('docs');
  }

  // Visibility for emergency sections on mobile
  isEmergencySectionVisible(sectionId: string): boolean {
    // Desktop or non-emergency → show all
    if (!this.showBottomNav || this.appMode !== 'emergency') return true;

    // HOME TAB: show Address + Contact only
    if (this.mobileActiveTab === 'home') {
      return sectionId === 'address' || sectionId === 'contact';
    }

    // INFO TAB:
    // If dashboard visible (no tile picked) → hide all sections
    if (this.mobileActiveTab === 'info' && !this.mobileInfoSectionId) return false;

    // Grouping rules based on the selected info tile/section
    const docsId = this.prefId('docs');

    // CARETAKER tile (previously "Contacts"):
    // show ONLY the caretaker card.
    if (this.mobileInfoSectionId === 'contacts-group') {
      return sectionId === 'caretaker';
    }

    // MANAGEMENT tile (formerly "Service hours"):
    // show company contact, property manager, technical assets manager
    // and both office & emergency availability sections.
    if (this.mobileInfoSectionId === 'availability' || this.mobileInfoSectionId === 'emergency-availability') {
      const managementIds = [
        'contact',
        'property-managers',
        'technical-assets-managers',
        'sev-managers',
        'accountants',
        'availability',
        'emergency-availability',
      ];
      return managementIds.includes(sectionId);
    }

    // EMERGENCY INSTRUCTIONS only
    if (this.mobileInfoSectionId === 'emergency-instructions') {
      return sectionId === 'emergency-instructions';
    }

    // EMERGENCY NUMBERS only
    if (this.mobileInfoSectionId === 'emergency-numbers') {
      return sectionId === 'emergency-numbers';
    }

    // TECHNICAL SERVICES only
    if (this.mobileInfoSectionId === 'technical-services') {
      return sectionId === 'technical-services';
    }

    // QUICK LINKS only
    if (this.mobileInfoSectionId === 'quick-links') {
      return sectionId === 'quick-links';
    }

    // DOCS only
    if (this.mobileInfoSectionId === docsId) {
      return sectionId === docsId;
    }

    return false;
  }

  // Show the "Back to dashboard" button when on mobile, Info tab, and a tile/section is selected
  get showInfoBackButton(): boolean {
    return this.showBottomNav && this.mobileActiveTab === 'info' && !!this.mobileInfoSectionId;
  }

  shouldShowCaretakerText(): boolean {
    // Mobile + emergency -> show only when user is on Info tab AND Contacts tile is selected
    if (this.showBottomNav && this.appMode === 'emergency') {
      return this.mobileActiveTab === 'info' && this.mobileInfoSectionId === 'contacts-group';
    }
    // Desktop or non-emergency -> keep current behavior (visible)
    return true;
  }

  get publicLandingPageMessage(): string | null {
    const value = this.company?.publicLandingPageMessage;
    if (typeof value !== 'string') {
      return null;
    }

    return value.trim() || null;
  }

  shouldShowPublicLandingPageMessage(): boolean {
    return !!this.publicLandingPageMessage && !this.mobileInfoSectionId;
  }

  // Reset the Info tab to the dashboard (tiles)
  backToDashboard(): void {
    this.mobileInfoSectionId = undefined;

    // Emergency mobile: return to the single hub screen (company card + feature cards)
    if (this.showBottomNav && this.appMode === 'emergency') {
      this.mobileActiveTab = 'home';
    }

    // Optional: scroll to top area to avoid leaving user mid-page
    setTimeout(() => this.scrollTo(this.prefId('top')), 0);
  }

  // Should the flyer have a border? (Contacts, hours, or emergency numbers on mobile; always on desktop)
  get showFlyerBorder(): boolean {
    if (!this.showBottomNav || this.appMode !== 'emergency') return true;
    if (this.mobileActiveTab !== 'info') return true; // Home tab
    const idsNeedingBorder = ['contacts-group', 'availability', 'emergency-availability', 'emergency-numbers'];
    return idsNeedingBorder.includes(this.mobileInfoSectionId || '');
  }

  // Does the flyer contain any visible content for the current mobile selection?
  get hasVisibleFlyerContentOnMobileInfo(): boolean {
    // IMPORTANT: On mobile emergency Info dashboard (no selected section),
    // the flyer area should be considered empty (otherwise it renders blank space).
    if (
      this.showBottomNav &&
      this.appMode === 'emergency' &&
      this.mobileActiveTab === 'info' &&
      !this.mobileInfoSectionId
    ) {
      return false;
    }

    if (!this.isMobileEmergencyInfo) return true; // keep default spacing on desktop/home
    // Sections that live inside the flyer container:
    const flyerIds = ['caretaker','address','contact','property-managers','technical-assets-managers','sev-managers','accountants','availability','emergency-availability','emergency-numbers'];
    return flyerIds.some(id => this.isEmergencySectionVisible(id));
  }

  // Are we on mobile, in emergency mode, inside the Info tab with a tile selected?
  get isMobileEmergencyInfo(): boolean {
    return this.showBottomNav &&
      this.appMode === 'emergency' &&
      this.mobileActiveTab === 'info' &&
      !!this.mobileInfoSectionId;
  }

  get hasAnyManagerContact(): boolean {
    const hasPM  = (this.propertyManagers?.length || 0) > 0 && !!this.company?.showPropertyManager;
    const hasTAM = (this.technicalAssetsManagers?.length || 0) > 0 && !!this.company?.showTechnicalAssetsManager;
    const hasSev = (this.sevManagers?.length || 0) > 0 && !!this.company?.showSevManager;
    const hasAccountant = (this.accountants?.length || 0) > 0 && !!this.company?.showAccountant;
    return !!(this.caretaker || hasPM || hasTAM || hasSev || hasAccountant);
  }

  get hasTechnicalServices(): boolean {
    return (this.technicalServiceGroups?.length || 0) > 0;
  }

  get showUnifiedNoContacts(): boolean {
    // Only on mobile + emergency + Info tab + "Managers & contact" tile
    const isMobileEmergencyContacts =
      this.showBottomNav &&
      this.appMode === 'emergency' &&
      this.mobileActiveTab === 'info' &&
      this.mobileInfoSectionId === 'contacts-group';

    return isMobileEmergencyContacts && !this.hasAnyManagerContact;
  }

  private computeHowToReachUsManagers(): any[] {
    if (!this.company?.showManagerInHowToReachUs) return [];
    if (!Array.isArray(this.company?.howToReachUsManagerOrder) || this.company.howToReachUsManagerOrder.length === 0) return [];

    const typeMap: Record<string, any[]> = {
      propertyManager: this.propertyManagers || [],
      technicalAssetsManager: this.technicalAssetsManagers || [],
      sevManager: this.sevManagers || [],
      accountant: this.accountants || [],
    };

    const result: any[] = [];
    for (const type of this.company.howToReachUsManagerOrder) {
      const managers = typeMap[type];
      if (managers && managers.length > 0) {
        for (const manager of managers) {
          result.push({ ...manager, managerType: type });
        }
      }
    }
    return result;
  }

  getManagerTypeLabelKey(type: string): string {
    const map: Record<string, string> = {
      propertyManager: 'MANAGER_TYPE_PROPERTY',
      technicalAssetsManager: 'MANAGER_TYPE_TECHNICAL_ASSETS',
      sevManager: 'MANAGER_TYPE_SEV',
      accountant: 'MANAGER_TYPE_ACCOUNTANT',
    };
    return map[type] || '';
  }

  private defineHospitalitySections(): SectionDef[] {
    return [
      { id: this.prefId('top'),  icon: 'home',  labelKey: 'LANDING.NAV.HOME',  visible: () => true },
      { id: 'host',              icon: 'phone', labelKey: 'LANDING.NAV.CONTACT', visible: () => !!this.hospitalityHostContact },
      { id: 'wifi',              icon: 'wifi',     labelKey: 'LANDING.NAV.WIFI',        visible: () => !!this.hospitalityWifi },
      { id: 'timeline',          icon: 'calendar', labelKey: 'LANDING.NAV.TIMELINE',    visible: () => this.hospitalityTimeline?.length > 0 },
      { id: 'house-rules',       icon: 'solution', labelKey: 'LANDING.NAV.HOUSE_RULES', visible: () => (this.hospitalityHouseRules?.length || 0) > 0 },
      { id: 'guides',            icon: 'book',     labelKey: 'LANDING.NAV.GUIDES',      visible: () => this.hospitalityGuides?.length > 0 },
      { id: 'feedback',          icon: 'message',  labelKey: 'LANDING.NAV.FEEDBACK',    visible: () => !!this.hospitalityFeedback },
      { id: 'info',              icon: 'info-circle', labelKey: 'LANDING.NAV.INFO',     visible: () => (this.hospitalityInfoSections?.length || 0) > 0 },
      { id: this.prefId('docs'), icon: 'file-text',   labelKey: 'LANDING.NAV.DOCS',     visible: () => (this.documentSections?.length || 0) > 0 || this.loadingDocs },
      { id: 'rating',            icon: 'star',        labelKey: 'LANDING.NAV.RATING',   visible: () => this.showHospitalityRatingCard || !!this.hospitalityRatingSummary },
    ];
  }

  private defineEmergencySections(): SectionDef[] {
    return [
      // Top / header
      {
        id: this.prefId('top'),
        icon: 'bank',
        labelKey: 'LANDING.NAV.TAB_HOME_EMERGENCY',
        visible: () => true
      },

      // Emergency instructions (public landing-page content)
      {
        id: 'emergency-instructions',
        icon: 'file-protect',
        labelKey: 'LANDING.NAV.EMERGENCY_INSTRUCTIONS',
        visible: () => (this.emergencyInstructions?.length || 0) > 0
      },

      // Caretaker only (formerly "Contacts")
      {
        id: 'contacts-group',
        icon: 'user',
        labelKey: 'LANDING.NAV.MANAGERS_CONTACT',
        visible: () => !!this.caretaker
      },

      // Management (formerly "Service hours"):
      // office hours + emergency hours; on mobile Info we also show
      // company contact + property + technical managers, see isEmergencySectionVisible.
      {
        id: this.getAvailabilitySectionId(),
        icon: 'team',
        labelKey: 'LANDING.NAV.OFFICE_EMERGENCY_HOURS',
        visible: () => this.hasAnyManagementInfo
      },

      // Emergency numbers
      {
        id: 'emergency-numbers',
        icon: 'safety',
        labelKey: 'LANDING.NAV.EMERGENCY_NUMBERS',
        visible: () => this.hasVisibleEmergencyNumbers
      },

      // Technical services
      {
        id: 'technical-services',
        icon: 'deployment-unit',
        labelKey: 'LANDING.NAV.SERVICES',
        visible: () => this.hasTechnicalServices
      },

      // Quick links
      {
        id: 'quick-links',
        icon: 'link',
        labelKey: 'LANDING.NAV.QUICK_LINKS',
        visible: () => (this.quickLinks?.length || 0) > 0
      },

      // Documents
      {
        id: this.prefId('docs'),
        icon: 'file-text',
        labelKey: 'LANDING.NAV.DOCS',
        visible: () => (this.documentSections?.length || 0) > 0 || this.loadingDocs
      },
    ];
  }

  /**
   * "Caretaker" nav entry: scroll only to the caretaker card.
   */
  private getManagersContactSectionId(): string {
    if (this.caretaker) {
      return 'caretaker';
    }
    // Fallback so scrollTo still has a valid target if caretaker is missing
    return 'contact';
  }

  /**
   * Decide which element the "Office & emergency hours" nav entry should scroll to.
   * If office hours exist, scroll to them; otherwise scroll to emergency hours.
   */
  private getAvailabilitySectionId(): string {
    if (this.showAvailability) {
      return 'availability';
    }
    if (this.showEmergencyAvailability) {
      return 'emergency-availability';
    }
    // Fallback to office-hours id
    return 'availability';
  }

  private buildSectionIndex(): void {
    const base = this.appMode === 'hospitality'
      ? this.defineHospitalitySections()
      : this.defineEmergencySections();

    this.sectionMenu = base.filter(s => s.visible());

    // Info dashboard menu (mobile "Info" tab)
    if (this.appMode === 'hospitality') {
      this.infoSectionMenu = this.sectionMenu.filter(s =>
        s.id !== this.prefId('top') && s.id !== 'local-tips'
      );
    } else {
      this.infoSectionMenu = this.sectionMenu.filter(s =>
        s.id !== this.prefId('top')
      );
    }

    const priorityIds = this.appMode === 'hospitality'
      ? [this.prefId('top'), 'host', 'wifi', 'timeline', this.prefId('docs')]
      : [
        this.prefId('top'),
        'contacts-group',
        'emergency-numbers',
        'technical-services',
        this.prefId('docs')
      ];

    this.bottomNav = priorityIds
      .map(id => this.sectionMenu.find(s => s.id === id))
      .filter((s): s is SectionDef => !!s)
      .slice(0, 5);

    if (!this.bottomNav.length) {
      const top = this.sectionMenu.find(s => s.id === this.prefId('top'));
      if (top) this.bottomNav = [top];
    }

    setTimeout(() => this.updateActiveSection(), 0);
  }

  protected prefId(id: string): string {
    return `${this.appMode}-${id}`;
  }

  private setupStorageKeys(): void {
    if (!this.companyId || !this.propertyId) {
      return;
    }
    this.announcementSeenStorageKey = `landing-announcements-seen-${this.companyId}-${this.propertyId}`;
    const stored = this.announcementSeenStorageKey
      ? this.getLocalStorageItem(this.announcementSeenStorageKey)
      : null;
    this.lastSeenAnnouncementsAt = stored ? parseInt(stored, 10) || 0 : 0;

    this.pushCtaDismissed = false;
    this.iosTipDismissedKey = `landing-ios-tip-${this.companyId}-${this.propertyId}`;
    this.iosTipDismissed = this.iosTipDismissedKey
      ? this.getLocalStorageItem(this.iosTipDismissedKey) === 'true'
      : false;

    // per-property hint key
    this.infoTabHintKey = `landing-info-hint-${this.companyId}-${this.propertyId}`;
    this.infoTabHintDismissed = this.infoTabHintKey
      ? this.getLocalStorageItem(this.infoTabHintKey) === 'true'
      : false;
    // per-property session keys (sessionStorage => resets when tab/browser session ends)
    this.dashboardPulseSeenKey = `landing-dashboard-pulse-seen-${this.companyId}-${this.propertyId}`;
    this.dashboardToastSeenKey = `landing-dashboard-toast-seen-${this.companyId}-${this.propertyId}`;

    // per-property session keys (sessionStorage => resets when tab/browser session ends)
    this.announcementsNudgeSeenKey = `landing-announcements-nudge-seen-${this.companyId}-${this.propertyId}`;
    this.announcementsPopupSeenKey = `landing-announcements-popup-seen-${this.companyId}-${this.propertyId}`;
  }

  private loadAnnouncements(): void {
    if (!this.companyId || !this.propertyId) {
      return;
    }
    this.announcementsLoading = true;
    this.announcementService.loadForLanding(this.companyId, this.propertyId).subscribe({
      next: (response) => {
        this.rawAnnouncements = response?.items || [];
        this.lastAnnouncementUpdated = response?.lastUpdated ? new Date(response.lastUpdated) : undefined;
        this.translateAnnouncements();
      },
      error: () => {
        this.rawAnnouncements = [];
        this.announcements = [];
        this.announcementsLoading = false;
        this.showAnnouncements = false;
      },
    });
  }

  private translateAnnouncements(): void {
    const translationRunId = this.translationRunId;
    const targetLang = this.selectedLanguage;
    if (!this.rawAnnouncements.length) {
      this.announcements = [];
      this.announcementsLoading = false;
      this.showAnnouncements = false;
      return;
    }

    this.announcementsLoading = true;
    from(this.rawAnnouncements).pipe(
      // Process 3 at a time instead of everything at once
      mergeMap((item: any) => this.translateAnnouncementItem(item, targetLang), 3),
      toArray()
    ).subscribe({
      next: (results: LandingAnnouncement[]) => {
        if (!this.isCurrentTranslationRun(translationRunId, targetLang)) {
          return;
        }
        this.announcements = results
          .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
        this.openAnnouncementsDrawerFromUrl();
        this.showAnnouncements = this.announcements.length > 0;
        this.announcementsLoading = false;
      },
      error: () => {
        if (!this.isCurrentTranslationRun(translationRunId, targetLang)) {
          return;
        }
        this.announcements = this.rawAnnouncements.map((item: any) => ({
          id: item.id,
          title: item.title,
          summary: item.summary,
          urgency: item.urgency || 'normal',
          updatedAt: item.updatedAt,
          validFrom: item.validFrom,
          validTo: item.validTo,
          attachments: this.normalizeAnnouncementAttachments(item.attachments),
          translatedTitle: item.title,
          translatedSummary: item.summary,
          isNew: new Date(item.updatedAt).getTime() > this.lastSeenAnnouncementsAt,
        }));
        this.openAnnouncementsDrawerFromUrl();
        this.showAnnouncements = this.announcements.length > 0;
        this.announcementsLoading = false;
      },
    });
  }

  protected override onLocalTipsUpdated(value: HospitalityLocalTipsViewModel | undefined): void {
    if (typeof window === 'undefined') {
      return;
    }
    if (!value || !value.mapMarkers?.length) {
      this.destroyLocalTipsMap();
      return;
    }
    this.scheduleLocalTipsMapUpdate();
  }

  private scheduleLocalTipsMapUpdate(): void {
    if (typeof window === 'undefined') {
      return;
    }
    if (!this.hospitalityLocalTips?.mapMarkers?.length) {
      this.destroyLocalTipsMap();
      return;
    }
    if (!this.localTipsMapContainer) {
      this.ngZone.runOutsideAngular(() => {
        setTimeout(() => this.scheduleLocalTipsMapUpdate(), 0);
      });
      return;
    }
    if (this.localTipsMapFrameRequested) {
      return;
    }
    this.localTipsMapFrameRequested = true;
    this.ngZone.runOutsideAngular(() => {
      requestAnimationFrame(() => {
        this.localTipsMapFrameRequested = false;
        this.renderLocalTipsMap().catch(() => {
          // ignore render errors; map will retry on next update
        });
      });
    });
  }

  private async renderLocalTipsMap(): Promise<void> {
    const container = this.localTipsMapContainer?.nativeElement;
    const tips = this.hospitalityLocalTips;
    if (!container || !tips?.mapMarkers?.length) {
      this.destroyLocalTipsMap();
      return;
    }

    // ✅ FIX: Angular can destroy/recreate the container when switching tabs (Info dashboard uses *ngIf).
    // Leaflet stays bound to the old DOM node -> white/blank map. Recreate map in that case.
    if (this.localTipsMap && this.localTipsMap.getContainer?.() !== container) {
      this.destroyLocalTipsMap();
    }

    const L = await this.getLeafletModule();
    if (!L) {
      return;
    }

    if (!this.localTipsMap) {
      container.innerHTML = '';
      this.localTipsMap = L.map(container, {
        attributionControl: false,
        zoomControl: false,
        closePopupOnClick: false,
        dragging: true,
        scrollWheelZoom: false,
      });
      this.localTipsTileLayer = L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '© OpenStreetMap contributors',
        maxZoom: 19,
      }).addTo(this.localTipsMap);
    }

    this.localTipsMapMarkers.forEach(marker => {
      if (marker?.remove) {
        marker.remove();
      }
    });
    this.localTipsMapMarkers = [];

    const bounds = L.latLngBounds([]);

    tips.mapMarkers.forEach(marker => {
      const color = marker.kind === 'property' ? '#2563eb' : '#dc2626';
      const circle = L.circleMarker([marker.lat, marker.lng], {
        radius: 7,
        color,
        fillColor: color,
        fillOpacity: 0.9,
        opacity: 1,
        weight: marker.kind === 'property' ? 2 : 1.5,
      }).addTo(this.localTipsMap);

      if (marker.label) {
        circle.bindTooltip(marker.label, { direction: 'top' });
      }
      if (marker.url) {
        const targetUrl = marker.url;
        circle.on('click', () => window.open(targetUrl, '_blank'));
        const element = circle.getElement();
        if (element) {
          element.setAttribute('role', 'button');
          element.setAttribute('tabindex', '0');
        }
      }

      bounds.extend([marker.lat, marker.lng]);
      this.localTipsMapMarkers.push(circle);
    });

    if (bounds.isValid()) {
      if (bounds.getNorthWest().equals(bounds.getSouthEast())) {
        this.localTipsMap.setView(bounds.getCenter(), 15);
      } else {
        this.localTipsMap.fitBounds(bounds.pad(0.3), { animate: false });
      }
    }

    this.localTipsMap.invalidateSize();
  }

  // Reflow + recenter the Local Tips map after it becomes visible again
  private refitLocalTipsMapOnShow(): void {
    const tips = this.hospitalityLocalTips;
    if (!tips?.mapMarkers?.length || !this.localTipsMap) return;

    const L = this.leafletModule;
    // If Leaflet hasn't been loaded/created yet, let the normal renderer handle it.
    if (!L) {
      this.scheduleLocalTipsMapUpdate();
      return;
    }

    // Let layout settle, then invalidate size and fit to bounds
    setTimeout(() => {
      try {
        this.localTipsMap.invalidateSize();
        const bounds = L.latLngBounds(tips.mapMarkers.map(m => [m.lat, m.lng] as [number, number]));
        if (!bounds.isValid()) return;

        if (bounds.getNorthWest().equals(bounds.getSouthEast())) {
          this.localTipsMap.setView(bounds.getCenter(), 15);
        } else {
          this.localTipsMap.fitBounds(bounds.pad(0.3), { animate: false });
        }
      } catch { /* no-op */ }
    }, 0);
  }

  private async getLeafletModule(): Promise<typeof import('leaflet') | undefined> {
    if (this.leafletModule) {
      return this.leafletModule;
    }
    if (typeof window === 'undefined') {
      return undefined;
    }
    try {
      const module = await import('leaflet');
      const resolved = (module as any)?.default ? (module as any).default : module;
      this.leafletModule = resolved;
      return this.leafletModule;
    } catch (error) {
      console.error('Failed to load Leaflet', error);
      return undefined;
    }
  }

  private destroyLocalTipsMap(): void {
    this.localTipsMapFrameRequested = false;
    this.localTipsMapMarkers.forEach(marker => {
      if (marker?.remove) {
        marker.remove();
      }
    });
    this.localTipsMapMarkers = [];
    if (this.localTipsTileLayer) {
      this.localTipsTileLayer.remove();
      this.localTipsTileLayer = undefined;
    }
    if (this.localTipsMap) {
      this.localTipsMap.remove();
      this.localTipsMap = undefined;
    }
  }

  private translateAnnouncementItem(item: any, targetLang: string) {
    const updatedAt = item.updatedAt || new Date().toISOString();
    const base: LandingAnnouncement = {
      id: item.id,
      title: item.title,
      summary: item.summary,
      urgency: item.urgency || 'normal',
      validFrom: item.validFrom,
      validTo: item.validTo,
      updatedAt,
      attachments: this.normalizeAnnouncementAttachments(item.attachments),
      translatedTitle: item.title,
      translatedSummary: item.summary,
      isNew: new Date(updatedAt).getTime() > this.lastSeenAnnouncementsAt,
    };

    const normalizedTargetLang = String(targetLang || '').toLowerCase();
    const sourceLang = String(item.language || 'de').toLowerCase();
    if (!normalizedTargetLang || normalizedTargetLang === sourceLang) {
      return of(base);
    }

    const storedTranslation = item?.translations?.[normalizedTargetLang];
    const storedTitle = typeof storedTranslation?.title === 'string' ? storedTranslation.title.trim() : '';
    const storedSummary = typeof storedTranslation?.summary === 'string' ? storedTranslation.summary.trim() : '';

    const translateWithFallback = (text: string) =>
      this.translationService.translateText(text, normalizedTargetLang, sourceLang).pipe(
        map((response) => response?.responseData?.translatedText?.trim() || text),
        catchError(() => of(text)),
      );

    return forkJoin([
      storedTitle ? of(storedTitle) : translateWithFallback(item.title),
      storedSummary ? of(storedSummary) : translateWithFallback(item.summary),
    ]).pipe(
      map(([title, summary]) => ({
        ...base,
        translatedTitle: title,
        translatedSummary: summary,
      })),
    );
  }

  private persistAnnouncementsSeen(): void {
    if (!this.announcementSeenStorageKey) {
      return;
    }
    const timestamp = Date.now();
    localStorage.setItem(this.announcementSeenStorageKey, `${timestamp}`);
    this.lastSeenAnnouncementsAt = timestamp;
  }

  private trackAnnouncementView(announcement: LandingAnnouncement): void {
    if (!announcement?.id || !announcement.isNew) {
      return;
    }

    this.track('announcement-view', {
      announcementId: announcement.id,
      announcementTitle: announcement.title,
    });
  }

  private markVisibleUnreadAnnouncementsViewed(): void {
    const seenIds = new Set<string>();
    const unreadAnnouncements = (this.announcements || []).filter((announcement) => {
      if (!announcement?.id || !announcement.isNew || seenIds.has(announcement.id)) {
        return false;
      }
      seenIds.add(announcement.id);
      return true;
    });

    if (!unreadAnnouncements.length) {
      return;
    }

    unreadAnnouncements.forEach((announcement) => this.trackAnnouncementView(announcement));
    this.persistAnnouncementsSeen();
    this.announcements = (this.announcements || []).map((announcement) => ({ ...announcement, isNew: false }));
    this.cdr.markForCheck();
  }

  private initializePush(): void {
    if (!this.companyId || !this.propertyId) {
      return;
    }
    if (!this.pushStateSubscription) {
      this.pushStateSubscription = this.pushService.stateChanges().subscribe((state) => {
        this.pushState = state;
        this.updatePushVisibility(state);
      });
    }
    this.pushService.init(this.companyId, this.propertyId).catch((error) => {
      console.warn('push init failed', error);
    });
  }

  private updatePushVisibility(state: PushCapabilityState): void {
    if (!state) {
      this.showPushCta = false;
      this.showIosInstallTip = false;
      this.showPushOptOut = false;
      return;
    }
    this.showIosInstallTip = state.isIosStandaloneRequired && !this.iosTipDismissed;
    this.showPushCta = state.supported && state.permission !== 'denied' && !state.subscribed && !this.pushCtaDismissed;
    this.showPushOptOut = state.supported && state.permission === 'granted' && state.subscribed;

    if (!this.pushStateLogged) {
      this.pushStateLogged = true;
    }
  }

  dismissPushCta(): void {
    this.pushCtaDismissed = true;
    this.showPushCta = false;
  }

  dismissIosTip(): void {
    this.iosTipDismissed = true;
    this.showIosInstallTip = false;
    if (this.iosTipDismissedKey) {
      localStorage.setItem(this.iosTipDismissedKey, 'true');
    }
  }

  openIosInstallVideoModal(): void {
    this.iosInstallVideoUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
      `https://player.vimeo.com/video/${this.IOS_INSTALL_VIMEO_VIDEO_ID}?autoplay=1&title=0&byline=0&portrait=0&dnt=1`
    );
    this.iosInstallVideoModalVisible = true;
    this.useBodyOverlays();

    if (!this.iosInstallVideoOverlayRef) {
      this.iosInstallVideoOverlayRef = this.overlay.create({
        hasBackdrop: false,
        disposeOnNavigation: true,
        positionStrategy: this.overlay.position().global().top('0').left('0'),
        scrollStrategy: this.overlay.scrollStrategies.block(),
        width: '100%',
        height: '100%',
      });
    }

    const overlayRef = this.iosInstallVideoOverlayRef;
    if (!overlayRef) {
      return;
    }

    if (!overlayRef.hasAttached()) {
      const portal = new ComponentPortal(LandingIosVideoOverlayComponent);
      const componentRef = overlayRef.attach(portal);
      componentRef.instance.videoUrl = this.iosInstallVideoUrl;
      componentRef.instance.closeRequested.subscribe(() => this.closeIosInstallVideoModal());
    }
  }

  closeIosInstallVideoModal(): void {
    this.iosInstallVideoModalVisible = false;
    this.iosInstallVideoUrl = undefined;
    this.disposeIosInstallVideoOverlay();
    if (this.showMobilePreview) {
      this.usePreviewOverlays();
    }
  }

  private disposeIosInstallVideoOverlay(): void {
    if (this.iosInstallVideoOverlayRef) {
      this.iosInstallVideoOverlayRef.dispose();
      this.iosInstallVideoOverlayRef = undefined;
    }
  }

  async enablePush(): Promise<void> {
    if (!this.companyId || !this.propertyId) {
      return;
    }
    try {
      const granted = await this.pushService.requestPermission(this.companyId, this.propertyId, this.selectedLanguage);
      if (granted) {
        this.track('push-subscribed', { language: this.selectedLanguage });
        this.dismissPushCta();
      }
    } catch (error: any) {
      // this.track('push-subscribe-failed', { message: error?.message });
    }
  }

  async disablePush(): Promise<void> {
    if (!this.companyId || !this.propertyId || this.optOutProcessing) {
      return;
    }

    this.optOutProcessing = true;
    try {
      this.pushCtaDismissed = false;
      await this.pushService.unsubscribe(this.companyId, this.propertyId);
      this.track('push-unsubscribed');
    } catch (error) {
      console.warn('push unsubscribe failed', error);
    } finally {
      this.optOutProcessing = false;
    }
  }

  openAnnouncement(announcement: LandingAnnouncement): void {
    if (!announcement?.id || !announcement.isNew) {
      return;
    }

    this.trackAnnouncementView(announcement);
    this.persistAnnouncementsSeen();
    this.announcements = (this.announcements || []).map((item) =>
      item.id === announcement.id ? { ...item, isNew: false } : item
    );
    this.cdr.markForCheck();
  }

  onAnnouncementItemKeydown(event: KeyboardEvent, announcement: LandingAnnouncement): void {
    if (event.target !== event.currentTarget) {
      return;
    }

    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }

    event.preventDefault();
    this.openAnnouncement(announcement);
  }

  openAnnouncementAttachment(announcement: LandingAnnouncement, file: DocumentSectionFile): void {
    if (!file?.url) {
      return;
    }

    this.trackDocumentView(
      announcement.translatedTitle || announcement.title || this.translate.instant('LANDING.ANNOUNCEMENTS.TITLE'),
      file.fileName
    );
    this.track('announcement-attachment-view', {
      announcementId: announcement.id,
      announcementTitle: announcement.title,
      fileName: file.fileName,
      fileType: file.fileType,
    });
    openDocumentSectionFile(file, (url) => this.openImagePreview(url));
  }

  isAnnouncementAttachmentImage(file: DocumentSectionFile): boolean {
    return isDocumentSectionImageFile(file);
  }

  private normalizeAnnouncementAttachments(files: any[] = []): DocumentSectionFile[] {
    return (Array.isArray(files) ? files : [])
      .filter((file) => file?.url && file?.fileName)
      .map((file) => ({
        fileName: file.fileName,
        fileType: file.fileType || '',
        url: file.url,
        s3Key: file.s3Key,
        sourceS3Key: file.sourceS3Key,
      }));
  }

  private openAnnouncementsDrawerFromUrl(): void {
    const noticeId = new URL(window.location.href).searchParams.get('notice');
    if (!noticeId) {
      return;
    }

    const announcement = (this.announcements || []).find((item) => item.id === noticeId);
    if (announcement) {
      this.notificationsDrawerOpen = true;
    }
  }

  protected track(type: string, metadata?: any) {
    if (!this.companyId || !this.propertyId) return;
    this.propertyService
      .trackInteraction(this.companyId, this.propertyId, { type: type as any, metadata })
      .subscribe(() => {}, err => console.warn('tracking failed', err));
  }

  trackEmergencyCall() {
    this.track('emergency-call');
  }

  trackContactClick(
    kind: 'phone' | 'mobile' | 'email' | 'whatsapp',
    value?: string,
    relatedTo?: 'company' | 'caretaker' | 'property-manager' | 'technical-assets-manager' | 'sev-manager' | 'accountant' | 'hospitality-host' | 'technical-service' | 'emergency-instruction',
    relatedName?: string
  ) {
    if (!value) return;

    this.track('contact-click', {
      kind,
      value,
      relatedTo: relatedTo || 'company',
      relatedName: relatedName || null
    });
  }

  caretakerContactPersonCountKey(): string {
    return this.caretakerContactPersons.length === 1
      ? 'COMPANY_PAGE.CARETAKER_CONTACT_PERSON_COUNT_ONE'
      : 'COMPANY_PAGE.CARETAKER_CONTACT_PERSON_COUNT_OTHER';
  }

  getCaretakerContactTrackingName(person: CaretakerContactPersonView): string {
    const serviceName = String(this.caretaker?.name || '').trim();
    const contactName = String(person?.name || '').trim();
    const contactId = String(person?._id || '').trim();
    return [serviceName, contactName, contactId].filter(Boolean).join(' · ');
  }

  getCaretakerContactDialNumber(value: string): string {
    const normalized = this.dePhone.normalize(value);
    return normalized.e164 || normalized.national || String(value || '').replace(/\s+/g, '');
  }

  private normalizeCaretakerContactPersons(caretaker: any): CaretakerContactPersonView[] {
    const source = Array.isArray(caretaker?.contactPersons)
      ? caretaker.contactPersons
      : (typeof caretaker?.contactPerson === 'string' && caretaker.contactPerson.trim()
        ? [{ name: caretaker.contactPerson.trim(), isPrimary: true }]
        : []);
    const seenIds = new Set<string>();

    return source
      .map((person: any, index: number) => ({
        _id: typeof person?._id === 'string' ? person._id.trim() : '',
        name: typeof person?.name === 'string' ? person.name.trim() : '',
        position: typeof person?.position === 'string' ? person.position.trim() : '',
        phone: typeof person?.phone === 'string' ? person.phone.trim() : '',
        mobile: typeof person?.mobile === 'string' ? person.mobile.trim() : '',
        email: typeof person?.email === 'string' ? person.email.trim() : '',
        isPrimary: person?.isPrimary === true,
        index,
      }))
      .filter((person: CaretakerContactPersonView) => {
        if (!person.name || (person._id && seenIds.has(person._id))) return false;
        if (person._id) seenIds.add(person._id);
        return true;
      })
      .sort((left: CaretakerContactPersonView, right: CaretakerContactPersonView) =>
        Number(right.isPrimary) - Number(left.isPrimary) || left.index - right.index
      );
  }

  trackSectionView(title: string) {
    this.track('section-view', { sectionTitle: title });
  }

  trackDocumentView(sectionTitle: string, fileName: string) {
    this.track('document-view', { sectionTitle, fileName });
  }

  trackQuickLinkClick(link: CompanyQuickLink) {
    this.track('link-click', { label: link.label, url: link.url });
  }

  private loadDocuments(): void {
    if (!this.propertyId) return;
    this.loadingDocs = true;
    this.propertyService.getPropertyDocumentsPublic(this.propertyId!)
      .subscribe({
        next: sections => {
          this.documentSections = (sections || []).map(section => ({
            title: section.title,
            files: (section.files || []).map(file => ({
              fileName: file.fileName,
              fileType: file.fileType || '',
              url: file.url,
              s3Key: file.s3Key
            }))
          }));
          this.translateSectionTitles();
          this.loadingDocs = false;
          this.buildSectionIndex();
        },
        error: () => {
          this.loadingDocs = false;
        }
      });
  }

  private translateSectionTitles(): void {
    if (!this.documentSections?.length) return;

    const targetLang = this.selectedLanguage;
    const translationRunId = this.translationRunId;

    this.documentSections.forEach((section, index) => {
      this.translationService.translateText(section.title, targetLang).pipe(
        map(res => (res?.responseData?.translatedText || section.title).trim()),
        catchError(() => of(section.title))
      ).subscribe(translated => {
        if (!this.isCurrentTranslationRun(translationRunId, targetLang)) {
          return;
        }
        // IMMUTABLE UPDATE so OnPush child sees a new Input reference
        const updated = { ...this.documentSections[index], translatedTitle: translated };
        this.documentSections = [
          ...this.documentSections.slice(0, index),
          updated,
          ...this.documentSections.slice(index + 1),
        ];
        this.cdr.markForCheck();
      });
    });
  }

  private translateQuickLinks(): void {
    // 🔒 Quick links are only shown in emergency mode
    if (this.appMode !== 'emergency' || !this.quickLinks?.length) {
      return;
    }

    const targetLang = this.selectedLanguage;
    const translationRunId = this.translationRunId;

    const requests = this.quickLinks.map(link =>
      this.translationService.translateText(link.label, targetLang).pipe(
        map(res => (res?.responseData?.translatedText || link.label).trim()),
        catchError(() => of(link.label))
      )
    );

    forkJoin(requests).subscribe(translatedLabels => {
      if (!this.isCurrentTranslationRun(translationRunId, targetLang)) {
        return;
      }
      translatedLabels.forEach((t, i) => {
        this.quickLinks[i].translatedLabel = t;
      });
      this.cdr.detectChanges();
    });
  }

  // ============================
  // Emergency Instructions (public)
  // ============================

  private loadEmergencyInstructions(): void {
    if (!this.companyId || !this.propertyId) return;
    if (this.appMode !== 'emergency') return;

    this.emergencyInstructionsLoading = true;
    this.emergencyInstructionsError = false;

    this.propertyService.getEmergencyInstructions(this.propertyId, this.companyId).subscribe({
      next: (res: any) => {
        this.rawEmergencyInstructions = this.normalizeEmergencyInstructions(res);

        // ✅ show section immediately (icon appears right after API returns)
        this.emergencyInstructions = [...this.rawEmergencyInstructions];
        this.armEmergencyInstructionTracking();
        this.buildSectionIndex();
        this.cdr.markForCheck();

        // then translate in background and replace with translated versions when done
        this.translateEmergencyInstructions();
      },
      error: () => {
        this.rawEmergencyInstructions = [];
        this.emergencyInstructions = [];
        this.emergencyInstructionsLoading = false;
        this.emergencyInstructionsError = true;
        this.cdr.markForCheck();
      }
    });
  }

  private normalizeEmergencyInstructions(res: any): EmergencyInstructionVM[] {
    const candidate =
      res?.items ??
      res?.instructions ??
      res?.emergencyInstructions ??
      res?.data ??
      res;

    if (!candidate) return [];

    // array of entries
    if (Array.isArray(candidate)) {
      return candidate
        .map((x: any, i: number) => this.normalizeEmergencyInstructionItem(x, i))
        .sort((first: EmergencyInstructionVM, second: EmergencyInstructionVM) => (first.order ?? 0) - (second.order ?? 0));
    }

    // single string
    if (typeof candidate === 'string') {
      return [{
        id: '0',
        category: 'other',
        title: this.translate.instant('LANDING.EMERGENCY_INSTRUCTIONS.UNTITLED'),
        who: '',
        how: candidate,
        showPhoneNumber: false,
        phone: undefined,
      }];
    }

    // single object
    if (typeof candidate === 'object') {
      return [this.normalizeEmergencyInstructionItem(candidate, 0)];
    }

    return [];
  }

  private normalizeEmergencyInstructionItem(x: any, index: number): EmergencyInstructionVM {
    const id = (x?._id ?? x?.id ?? `${index}`).toString();

    const category = (x?.category ?? 'other').toString().trim().toLowerCase();
    const title = (x?.title ?? x?.label ?? x?.name ?? '').toString().trim();

    const who = (x?.who ?? '').toString().trim();

    const howRaw = (x?.how ?? x?.text ?? x?.instruction ?? x?.body ?? x?.content ?? '').toString().trim();
    const how = howRaw || null;

    const showPhoneNumber = Boolean(x?.showPhoneNumber);
    const phoneRaw = (x?.phone ?? '').toString().trim();
    const phone = showPhoneNumber && phoneRaw ? phoneRaw : undefined;

    const sourceLanguage = ((x?.sourceLanguage ?? x?.language ?? x?.lang ?? 'de') as string).toLowerCase() as 'de' | 'en';
    const summary = (x?.summary ?? '').toString().trim() || null;
    const version = x?.version === 'v2' ? 'v2' : undefined;
    const steps = Array.isArray(x?.steps)
      ? x.steps.map((step: any, stepIndex: number): EmergencyInstructionStepVM => ({
        id: (step?.id ?? `${id}-step-${stepIndex}`).toString(),
        order: Number.isFinite(Number(step?.order)) ? Number(step.order) : stepIndex,
        title: (step?.title ?? '').toString().trim(),
        description: this.toOptionalEmergencyInstructionMultilineText(step?.description),
        iconKey: (step?.iconKey ?? '').toString().trim() || null,
        urgency: (step?.urgency ?? 'normal').toString().trim().toLowerCase() as 'normal' | 'important' | 'critical',
        action: step?.action ? {
          type: (step.action.type ?? 'link').toString().trim().toLowerCase() as 'phone' | 'email' | 'link' | 'document',
          label: (step.action.label ?? '').toString().trim() || null,
          phone: (step.action.phone ?? '').toString().trim() || null,
          email: (step.action.email ?? '').toString().trim() || null,
          url: (step.action.url ?? '').toString().trim() || step.action.document?.url || null,
          document: step.action.document ? {
            fileName: step.action.document.fileName,
            fileType: step.action.document.fileType,
            url: step.action.document.url,
            s3Key: step.action.document.s3Key,
            sourceS3Key: step.action.document.sourceS3Key,
            uploadedAt: step.action.document.uploadedAt,
          } : null,
          openInNewTab: Boolean(step.action.openInNewTab),
        } : null,
      })).sort((first: EmergencyInstructionStepVM, second: EmergencyInstructionStepVM) => first.order - second.order)
      : [];

    return {
      id,
      order: Number.isFinite(Number(x?.order)) ? Number(x.order) : index,
      category,
      title,
      who,
      how,
      showPhoneNumber,
      phone,
      language: sourceLanguage,
      sourceLanguage,
      version,
      scenarioKey: (x?.scenarioKey ?? '').toString().trim().toLowerCase() || undefined,
      iconKey: (x?.iconKey ?? '').toString().trim() || null,
      urgency: (x?.urgency ?? 'normal').toString().trim().toLowerCase() as 'normal' | 'important' | 'critical',
      summary,
      steps,
    };
  }

  private normalizeEmergencyInstructionMultilineText(value: unknown): string {
    return typeof value === 'string' ? value.replace(/\r\n?/g, '\n') : '';
  }

  private toOptionalEmergencyInstructionMultilineText(value: unknown): string | null {
    const normalized = this.normalizeEmergencyInstructionMultilineText(value);
    return normalized.trim() ? normalized : null;
  }

  private translateEmergencyInstructions(): void {
    if (this.appMode !== 'emergency') return;

    const items = this.rawEmergencyInstructions || [];
    const translationRunId = this.translationRunId;

    if (!items.length) {
      this.emergencyInstructions = [];
      this.emergencyInstructionsLoading = false;
      this.buildSectionIndex();
      this.cdr.markForCheck();
      return;
    }

    const targetLang = this.selectedLanguage;
    this.emergencyInstructionsLoading = true;

    from(items).pipe(
      mergeMap((item) => this.translateEmergencyInstructionItem(item, targetLang), 3),
      toArray()
    ).subscribe({
      next: (translated) => {
        if (!this.isCurrentTranslationRun(translationRunId, targetLang)) {
          return;
        }
        this.emergencyInstructions = translated;
        this.armEmergencyInstructionTracking();
        this.emergencyInstructionsLoading = false;
        this.emergencyInstructionsError = false;
        this.buildSectionIndex();
        this.cdr.markForCheck();
      },
      error: () => {
        if (!this.isCurrentTranslationRun(translationRunId, targetLang)) {
          return;
        }
        // fallback = show original
        this.emergencyInstructions = items;
        this.armEmergencyInstructionTracking();
        this.emergencyInstructionsLoading = false;
        this.emergencyInstructionsError = false;
        this.buildSectionIndex();
        this.cdr.markForCheck();
      }
    });
  }

  private translateEmergencyInstructionItem(item: EmergencyInstructionVM, targetLang: string) {
    const sourceLang = item.sourceLanguage || item.language || 'de';
    if (!targetLang || targetLang === sourceLang) return of(item);

    const translateWithFallback = (text?: string | null) => {
      const value = (text ?? '').trim();
      if (!value) return of(value);

      return this.translationService.translateText(value, targetLang, sourceLang).pipe(
        map((response) => (response?.responseData?.translatedText?.trim() || value)),
        catchError(() => of(value))
      );
    };

    return forkJoin([
      translateWithFallback(item.title),
      translateWithFallback(item.who),
      translateWithFallback(item.how),
      translateWithFallback(item.summary),
      item.steps?.length
        ? forkJoin(item.steps.map((step) => forkJoin([
          translateWithFallback(step.title),
          translateWithFallback(step.description),
          translateWithFallback(step.action?.label),
        ]).pipe(
          map(([translatedTitle, translatedDescription, translatedLabel]) => ({
            ...step,
            translatedTitle,
            translatedDescription,
            action: step.action ? {
              ...step.action,
              translatedLabel,
            } : step.action,
          }))
        )))
        : of([]),
    ]).pipe(
      map(([title, who, how, summary, steps]) => ({
        ...item,
        translatedTitle: title,
        translatedWho: who,
        translatedHow: how,
        translatedSummary: summary,
        steps,
      }))
    );
  }

  private clearTranslatedTechnicalServiceNotes(): void {
    (this.technicalServiceGroups || []).forEach((group: any) => {
      (group?.services || []).forEach((service: any) => {
        (service?.companies || []).forEach((company: any) => {
          company.translatedServiceNote = '';
        });
      });
    });
  }

  private translateTechnicalServiceNotes(): void {
    this.clearTranslatedTechnicalServiceNotes();

    if (this.appMode !== 'emergency' || !this.technicalServiceGroups?.length || !this.selectedLanguage) {
      return;
    }

    const targetLang = this.selectedLanguage;
    const translationRunId = this.translationRunId;

    const noteEntries = this.technicalServiceGroups.flatMap((group: any) =>
      (group?.services || []).flatMap((service: any) =>
        (service?.companies || [])
          .map((company: any) => ({
            company,
            note: typeof company?.serviceNote === 'string' ? company.serviceNote.trim() : ''
          }))
          .filter((entry: any) => !!entry.note)
      )
    );

    if (!noteEntries.length) {
      return;
    }

    from(noteEntries).pipe(
      mergeMap(({ company, note }) =>
        this.translationService.translateText(note, targetLang).pipe(
          map((response) => ({
            company,
            translatedNote: response?.responseData?.translatedText?.trim() || ''
          })),
          catchError(() => of({ company, translatedNote: '' }))
        ),
      3),
      toArray()
    ).subscribe({
      next: (results) => {
        if (!this.isCurrentTranslationRun(translationRunId, targetLang)) {
          return;
        }
        results.forEach(({ company, translatedNote }) => {
          company.translatedServiceNote = translatedNote;
        });
        this.cdr.markForCheck();
      },
      error: () => {
        if (!this.isCurrentTranslationRun(translationRunId, targetLang)) {
          return;
        }
        this.cdr.markForCheck();
      }
    });
  }

  private translateCaretakerNote(): void {
    this.translatedCaretakerNote = '';

    const note = typeof this.caretaker?.note === 'string' ? this.caretaker.note.trim() : '';
    if (!note || !this.selectedLanguage) {
      return;
    }

    const targetLang = this.selectedLanguage;
    const translationRunId = this.translationRunId;

    this.translationService.translateText(note, targetLang).pipe(
      map((response) => response?.responseData?.translatedText?.trim() || ''),
      catchError(() => of(''))
    ).subscribe({
      next: (translatedNote) => {
        if (!this.isCurrentTranslationRun(translationRunId, targetLang)) {
          return;
        }
        this.translatedCaretakerNote = translatedNote;
        this.cdr.markForCheck();
      },
      error: () => {
        if (!this.isCurrentTranslationRun(translationRunId, targetLang)) {
          return;
        }
        this.cdr.markForCheck();
      }
    });
  }

  openImagePreview(imageUrl: string): void {
    this.usePreviewOverlays(); // ✅ ensure image preview renders inside preview
    const images: NzImage[] = [{ src: imageUrl, alt: '' }];
    this.imageService.preview(images, { nzZoom: 1, nzRotate: 0 });
  }

  changeLanguage(language: string): void {
    // 🔴 IMPORTANT: reset queued/inflight translation jobs
    this.translationRunId += 1;
    this.translationService.resetForLanguageChange();

    this.selectedLanguage = language;
    this.translate.use(language);
    localStorage.setItem('selectedLandingPageLanguage', language);
    this.translateUserText();
    this.updateErrorMessages();
    this.translateSectionTitles();
    this.translateAnnouncements();
    this.translateQuickLinks();
    this.translateTechnicalServiceNotes();
    this.translateCaretakerNote();

    // Rebuild availability labels/times so weekdays + time strings follow the new language
    if (this.caretaker?.availability) {
      this.caretakerFormattedAvailability = this.formatOfficeHours(this.caretaker.availability);
    }

    if (this.company?.showAvailability && this.company?.availability) {
      this.formattedAvailability = this.formatAvailability(this.company.availability);
    }

    if (this.company?.showEmergencyAvailability && this.company?.emergencyAvailability) {
      this.formattedEmergencyAvailability = this.formatAvailability(this.company.emergencyAvailability);
    }

    // 🔒 Emergency-only: translate emergency instructions free text
    this.translateEmergencyInstructions();
    // 🔒 Hospitality-only texts
    if (this.appMode === 'hospitality') {
      this.buildHospitalityViewModels();
    }
    this.buildSectionIndex();
    this.track('language-change', { language });
  }

  private isCurrentTranslationRun(translationRunId: number, language: string): boolean {
    return translationRunId === this.translationRunId && language === this.selectedLanguage;
  }

  private prepareTechnicalServices(
    services: any[] | undefined,
    order: TechnicalServiceTypeOrder,
    categoryOrder: TechnicalServiceCategoryOrder
  ): {
    groups: Array<{ key: TechnicalServiceCategoryKey; services: any[] }>;
    flat: any[];
    order: TechnicalServiceTypeOrder;
    categoryOrder: TechnicalServiceCategoryOrder;
  } {
    if (!Array.isArray(services)) {
      return { groups: [], flat: [], order, categoryOrder: normalizeTechnicalServiceCategoryOrder(categoryOrder) };
    }

    const serviceTypeHints = new Map<string, TechnicalServiceCategoryKey>();
    const availableTypes: string[] = [];

    const normalizedServices = services
      .filter(service => service && typeof service === 'object')
      .flatMap((service) => {
        const normalizedService = normalizeServiceContact(service);
        const displayTypes = normalizedService.serviceType
          ? [normalizedService.serviceType]
          : [];

        return displayTypes.map((type) => {
          availableTypes.push(type);
          const category = getCategoryForServiceType(type);
          serviceTypeHints.set(type, category);
          return {
            ...normalizedService,
            serviceType: type,
            serviceCategory: category
          };
        });
      });

    const sanitizedOrder = normalizeTechnicalServiceOrder(order, serviceTypeHints, availableTypes);
    const sanitizedCategoryOrder = normalizeTechnicalServiceCategoryOrder(categoryOrder);

    const typeCategoryFromOrder = new Map<string, TechnicalServiceCategoryKey>();
    for (const categoryKey of sanitizedCategoryOrder) {
      (sanitizedOrder[categoryKey] || []).forEach((type: string) => {
        if (!typeCategoryFromOrder.has(type)) {
          typeCategoryFromOrder.set(type, categoryKey);
        }
      });
    }

    const flattenedOrder = flattenTechnicalServiceOrder(sanitizedOrder, sanitizedCategoryOrder);
    const orderMap = new Map<string, number>(flattenedOrder.map((type, index) => [type, index]));

    const sortedServices = [...normalizedServices].sort((a, b) => {
      const typeA = (a?.serviceType || '').trim();
      const typeB = (b?.serviceType || '').trim();
      const indexA = orderMap.has(typeA) ? orderMap.get(typeA)! : Number.MAX_SAFE_INTEGER;
      const indexB = orderMap.has(typeB) ? orderMap.get(typeB)! : Number.MAX_SAFE_INTEGER;
      if (indexA !== indexB) {
        return indexA - indexB;
      }
      return typeA.localeCompare(typeB, 'de-DE', { sensitivity: 'base' });
    });

    const groupedByType = new Map<string, any>();
    const seenServiceIdsByType = new Map<string, Set<string>>();
    sortedServices.forEach((service) => {
      const type = (service?.serviceType || '').trim();
      if (!type) {
        return;
      }
      const category = typeCategoryFromOrder.get(type) || getCategoryForServiceType(type);
      if (!groupedByType.has(type)) {
        groupedByType.set(type, {
          serviceType: type,
          serviceCategory: category,
          companies: []
        });
        seenServiceIdsByType.set(type, new Set<string>());
      }
      const identity = this.getTechnicalServiceIdentityKey(service, type);
      const seen = seenServiceIdsByType.get(type)!;
      if (seen.has(identity)) {
        return;
      }
      seen.add(identity);
      groupedByType.get(type).companies.push(service);
    });

    const groupedServices = Array.from(groupedByType.values());
    const groups = sanitizedCategoryOrder.map((key) => {
      const servicesInCategory = groupedServices.filter((serviceGroup) => serviceGroup.serviceCategory === key);
      return { key, services: servicesInCategory };
    }).filter(group => group.services.length > 0);

    return {
      groups,
      flat: groupedServices,
      order: sanitizedOrder,
      categoryOrder: sanitizedCategoryOrder
    };
  }

  private getTechnicalServiceIdentityKey(service: any, serviceType: string): string {
    const serviceId = service?._id || service?.id;
    if (serviceId) {
      return `${serviceType}::${String(serviceId)}`;
    }

    const serviceName = String(service?.serviceName || '').trim().toLowerCase();
    const phone = String(service?.phone || '').trim();
    const mobile = String(service?.mobile || '').trim();
    return `${serviceType}::${serviceName}::${phone}::${mobile}`;
  }

  getServiceIcon(serviceType: string): string {
    switch (serviceType) {
      case 'Winter Service':
        return 'cloud';
      case 'Heating':
        return 'fire';
      case 'Sanitary':
        return 'medicine-box';
      case 'Electrical':
        return 'bulb';
      case 'Smoke Detector':
        return 'alert';
      case 'Elevator':
        return 'up-circle';
      case 'Garage Door':
        return 'gateway';
      case 'Pipe Blockage':
        return 'tool';
      case 'Pipe Burst & Water Damage':
        return 'tool';
      case 'Cable Television':
        return 'video-camera';
      case 'Meter Reading Service':
        return 'number';
      case 'Bulk Waste Collection':
        return 'delete';
      case 'Gas Supply/Emergency Service':
        return 'alert';
      case 'Power Supply/Emergency Service':
        return 'thunderbolt';
      case 'Water Supply':
        return 'experiment';
      case 'Police':
        return 'safety';
      case 'Stairwell Cleaning':
        return 'home';
      case 'Locksmith Service':
        return 'key';
      case 'Chimney Sweep':
        return 'build';
      case 'Garden Maintenance':
        return 'environment';
      case 'Pest Control':
        return 'bug';
      case 'Energy Supply':
        return 'thunderbolt';
      case 'Waste Disposal':
        return 'delete';
      case 'Building Insurance':
        return 'insurance';
      case 'Gas Emergency/Gas Odor':
        return 'alert';
      case 'Fiber / Internet':
        return 'wifi';
      case 'Recyclable Waste Disposal':
        return 'sync';
      case 'Heating Oil Supply':
        return 'container';
      case 'District Heating Supplier':
        return 'fire';
      case 'Lifting Systems and Pumps':
        return 'deployment-unit';
      case 'Electric Storage Heater':
        return 'thunderbolt';
      case 'Locking System Maintenance':
        return 'lock';
      case 'Green Roof Maintenance':
        return 'environment';
      case 'Rental Mat Service':
        return 'appstore';
      case 'Street and Sidewalk Cleaning':
        return 'home';
      case 'Local Heating Supplier':
        return 'fire';
      case 'Double Parker':
        return 'car';
      case 'Gate/Gate Systems':
        return 'gateway';
      case 'Sewer/Blockage':
        return 'tool';
      case 'Solar System/PVA':
        return 'thunderbolt';
      case 'Ventilation':
        return 'tool';
      case 'Apartment Transfer Station (DHW/Heating)':
        return 'build';
      case 'Smoke and Heat Extraction System':
        return 'alert';
      case 'Fire Extinguisher':
        return 'fire';
      case 'Fire Doors':
        return 'safety';
      case 'Fire Alarm System':
        return 'alert';
      case 'Heating Cost Allocator':
        return 'number';
      case 'WE Meter':
        return 'number';
      case 'Coating (UG)':
        return 'tool';
      case 'Windows':
        return 'appstore';
      case 'Roller Shutter/Raffstore':
        return 'tool';
      case 'Doors':
        return 'appstore';
      case 'Roofer':
        return 'build';
      case 'Property Developer':
        return 'bank';
      case 'Green Pruning':
        return 'environment';
      default:
        return 'tool';
    }
  }

  formatAvailability(availability: any): any[] {
    if (!availability || typeof availability !== 'object') return [];

    type TimeRangeVm = { startTime: string; endTime: string; startMs: number; endMs: number };
    type DisplayTimeRange = { startTime: string; endTime: string };

    interface DayPattern {
      dayIndex: number;
      times: DisplayTimeRange[];
      key: string;
    }

    const dayPatterns: DayPattern[] = [];

    // 1) Build the "pattern" (full-day time list) for each day
    Object.keys(availability).forEach((dayName) => {
      const dayIndex = this.weekdayIndex(dayName);
      if (dayIndex > 6) return;

      const ranges = Array.isArray(availability[dayName]) ? availability[dayName] : [];

      const timesWithSortKey: TimeRangeVm[] = ranges
        .filter((r: any) => r && r.start && r.end)
        .map((r: any) => {
          const start = new Date(r.start);
          const end = new Date(r.end);
          return {
            startTime: this.formatTimeLocalized(r.start),
            endTime: this.formatTimeLocalized(r.end),
            startMs: start.getTime(),
            endMs: end.getTime(),
          };
        })
        .filter(t => t.startTime && t.endTime && !isNaN(t.startMs) && !isNaN(t.endMs))
        .sort((a, b) => a.startMs - b.startMs);

      if (!timesWithSortKey.length) return;

      const times: DisplayTimeRange[] = timesWithSortKey.map(t => ({
        startTime: t.startTime,
        endTime: t.endTime
      }));

      // key that represents the whole-day pattern of time ranges
      const key = times.map(t => `${t.startTime}-${t.endTime}`).join('|');

      dayPatterns.push({ dayIndex, times, key });
    });

    // 2) Group days that share the same full-day pattern
    const grouped: Record<string, { dayIndices: number[]; times: DisplayTimeRange[] }> = {};

    dayPatterns.forEach((p) => {
      if (!grouped[p.key]) {
        grouped[p.key] = { dayIndices: [], times: p.times };
      }
      grouped[p.key].dayIndices.push(p.dayIndex);
    });

    // 3) Build final array; sort by first weekday in group
    const result = Object.values(grouped)
      .map(g => {
        const dayIndices = Array.from(new Set(g.dayIndices)).sort((a, b) => a - b);
        return {
          dayIndices,
          daysLabel: this.formatDayIndicesAsRanges(dayIndices),
          times: g.times,
        };
      })
      .sort((a, b) => (a.dayIndices[0] ?? 99) - (b.dayIndices[0] ?? 99));

    return result;
  }

  private getIntlLocale(lang: string = this.selectedLanguage): string {
    switch ((lang || 'de').toLowerCase()) {
      case 'de': return 'de-DE';
      case 'en': return 'en-GB';
      case 'tr': return 'tr-TR';
      case 'uk': return 'uk-UA';
      case 'ar': return 'ar-SA';
      default: return 'de-DE';
    }
  }

  private weekdayIndex(dayName: string): number {
    const key = (dayName || '').toLowerCase().trim();
    return WEEKDAY_INDEX_BY_LOWER[key] ?? 99;
  }

  private weekdayShortLabel(index: number): string {
    if (index < 0 || index > 6) return '';

    const locale = this.getIntlLocale();

    // 2024-01-01 is a Monday (UTC). Add index days to get Tue/Wed/...
    const d = new Date(Date.UTC(2024, 0, 1 + index));

    try {
      return new Intl.DateTimeFormat(locale, { weekday: 'short' }).format(d);
    } catch {
      // ultra-safe fallback
      const fallback = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
      return fallback[index] || '';
    }
  }

  private formatTimeLocalized(dateString: string): string {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';

    const locale = this.getIntlLocale();

    try {
      // Keep 24h everywhere (remove hour12:false if you want locale AM/PM in English/Arabic)
      return new Intl.DateTimeFormat(locale, {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      }).format(date);
    } catch {
      // last-resort fallback
      return formatDate(date, 'HH:mm', 'en-US');
    }
  }

  private formatDayIndicesAsRanges(dayIndices: number[]): string {
    if (!Array.isArray(dayIndices) || dayIndices.length === 0) return '';

    const ordered = Array.from(new Set(dayIndices))
      .filter(i => i >= 0 && i <= 6)
      .sort((a, b) => a - b);

    const parts: string[] = [];

    let start = ordered[0];
    let prev = ordered[0];

    for (let i = 1; i < ordered.length; i++) {
      const cur = ordered[i];

      // consecutive day -> extend range
      if (cur === prev + 1) {
        prev = cur;
        continue;
      }

      // close current range
      parts.push(
        start === prev
          ? this.weekdayShortLabel(start)
          : `${this.weekdayShortLabel(start)} - ${this.weekdayShortLabel(prev)}`
      );

      // start new range
      start = cur;
      prev = cur;
    }

    // close last range
    parts.push(
      start === prev
        ? this.weekdayShortLabel(start)
        : `${this.weekdayShortLabel(start)} - ${this.weekdayShortLabel(prev)}`
    );

    return parts.join(', ');
  }

  formatOfficeHours(availability: any): any[] {
    if (!availability || typeof availability !== 'object') {
      return [];
    }

    type OfficeHoursGroup = { dayIndices: number[]; startTime: string; endTime: string };
    const groups: Record<string, OfficeHoursGroup> = {};

    // Loop through each day's availability
    for (const dayName of Object.keys(availability)) {
      const dayIndex = this.weekdayIndex(dayName);
      if (dayIndex > 6) continue;

      const start = availability[dayName]?.start;
      const end = availability[dayName]?.end;

      // Skip days with empty start or end times
      if (!start || !end) continue;

      const startTime = this.formatTimeLocalized(start);
      const endTime = this.formatTimeLocalized(end);
      if (!startTime || !endTime) continue;

      const key = `${startTime}-${endTime}`;

      if (!groups[key]) {
        groups[key] = { dayIndices: [], startTime, endTime };
      }
      groups[key].dayIndices.push(dayIndex);
    }

    return Object.values(groups)
      .map(g => {
        const dayIndices = Array.from(new Set(g.dayIndices)).sort((a, b) => a - b);
        return {
          days: this.formatDayIndicesAsRanges(dayIndices), // template uses {{ group.days }}
          startTime: g.startTime,
          endTime: g.endTime,
          _sort: dayIndices[0] ?? 99
        };
      })
      .sort((a: any, b: any) => a._sort - b._sort)
      .map(({ _sort, ...rest }: any) => rest);
  }

  formatGermanPhoneNumber(phone?: string, defaultNdc?: string): string {
    if (!phone) return '';
    return this.dePhone.normalize(phone, { defaultNdc }).display;
  }

  hasTechnicalServiceContactNumber(service: any): boolean {
    return Boolean(
      String(service?.phone || '').trim()
      || String(service?.mobile || '').trim()
      || String(service?.emergencyNumber || '').trim()
    );
  }

  getTranslatedServiceType(serviceType: string): string {
    if (!serviceType) {
      return '';
    }
    const translationKey = serviceTypeTranslationKeys[serviceType];
    if (translationKey) {
      const resolved = this.translate.instant('TECHNICAL_SERVICES.' + translationKey);
      if (resolved && resolved !== `TECHNICAL_SERVICES.${translationKey}`) {
        return resolved;
      }
    }
    const fallbackKey = serviceType.replace(/[^A-Za-z0-9]+/g, '_').replace(/^_+|_+$/g, '').toUpperCase();
    if (fallbackKey) {
      const resolvedFallback = this.translate.instant(`TECHNICAL_SERVICES.${fallbackKey}`);
      if (resolvedFallback && resolvedFallback !== `TECHNICAL_SERVICES.${fallbackKey}`) {
        return resolvedFallback;
      }
    }
    return serviceType;
  }

  private isValidObjectId(id: string | null | undefined): boolean {
    // 24 hex chars
    return !!id && /^[a-f\d]{24}$/i.test(id);
  }

  reloadPage(): void {
    window.location.reload();
  }

  translateUserText() {
    // 🔒 Only translate these texts in emergency mode
    if (this.appMode !== 'emergency' || !this.company) {
      return;
    }

    const targetLang = this.selectedLanguage;
    const translationRunId = this.translationRunId;

    if (this.company.caretakerText) {
      this.translationService
        .translateText(this.company.caretakerText, targetLang)
        .subscribe(response => {
          if (!this.isCurrentTranslationRun(translationRunId, targetLang)) {
            return;
          }
          this.translatedCaretakerText =
            response.responseData.translatedText || this.company.caretakerText;
        });
    }

    if (this.company.publicLandingPageMessage) {
      this.translationService
        .translateText(this.company.publicLandingPageMessage, targetLang)
        .subscribe(response => {
          if (!this.isCurrentTranslationRun(translationRunId, targetLang)) {
            return;
          }
          this.translatedPublicLandingPageMessage =
            response.responseData.translatedText || this.company.publicLandingPageMessage;
        });
    }

    if (this.company.technicalServiceText) {
      this.translationService
        .translateText(this.company.technicalServiceText, targetLang)
        .subscribe(response => {
          if (!this.isCurrentTranslationRun(translationRunId, targetLang)) {
            return;
          }
          this.translatedTechnicalServiceText =
            response.responseData.translatedText || this.company.technicalServiceText;
        });
    }
  }

  updateErrorMessages(): void {
    if (this.nzErrorStatus === '403') {
      this.errorTitle = this.translate.instant('COMPANY_PAGE.PROPERTY_FORBIDDEN_TITLE');
      this.errorSubtitle = this.translate.instant('COMPANY_PAGE.PROPERTY_FORBIDDEN_MESSAGE');
    } else if (this.nzErrorStatus === '404') {
      this.errorTitle = this.translate.instant('COMPANY_PAGE.PROPERTY_NOT_FOUND_TITLE');
      this.errorSubtitle = this.translate.instant('COMPANY_PAGE.PROPERTY_NOT_FOUND_MESSAGE');
    } else if (this.nzErrorStatus === 'warning') {
      this.errorTitle = this.translate.instant('COMPANY_PAGE.INVALID_REQUEST_TITLE');
      this.errorSubtitle = this.translate.instant('COMPANY_PAGE.INVALID_REQUEST_MESSAGE');
    } else if (this.nzErrorStatus === '500') {
      this.errorTitle = this.translate.instant('COMPANY_PAGE.GENERIC_ERROR_TITLE');
      this.errorSubtitle = this.translate.instant('COMPANY_PAGE.GENERIC_ERROR_MESSAGE');
    }
    this.cdr.detectChanges();
  }

  getErrorTitle(): string {
    if (this.nzErrorStatus === '403') {
      return this.translate.instant('COMPANY_PAGE.PROPERTY_FORBIDDEN_TITLE');
    } else if (this.nzErrorStatus === '404') {
      return this.translate.instant('COMPANY_PAGE.PROPERTY_NOT_FOUND_TITLE');
    } else if (this.nzErrorStatus === 'warning') {
      return this.translate.instant('COMPANY_PAGE.INVALID_REQUEST_TITLE');
    } else if (this.nzErrorStatus === '500') {
      return this.translate.instant('COMPANY_PAGE.GENERIC_ERROR_TITLE');
    }
    return '';
  }

  getErrorSubtitle(): string {
    if (this.nzErrorStatus === '403') {
      return this.translate.instant('COMPANY_PAGE.PROPERTY_FORBIDDEN_MESSAGE');
    } else if (this.nzErrorStatus === '404') {
      return this.translate.instant('COMPANY_PAGE.PROPERTY_NOT_FOUND_MESSAGE');
    } else if (this.nzErrorStatus === 'warning') {
      return this.translate.instant('COMPANY_PAGE.INVALID_REQUEST_MESSAGE');
    } else if (this.nzErrorStatus === '500') {
      return this.translate.instant('COMPANY_PAGE.GENERIC_ERROR_MESSAGE');
    }
    return '';
  }

  private hasSeenAnnouncementsPopup(): boolean {
    if (!this.announcementsPopupSeenKey) return false;
    return sessionStorage.getItem(this.announcementsPopupSeenKey) === 'true';
  }

  private markAnnouncementsPopupSeen(): void {
    if (!this.announcementsPopupSeenKey) return;
    sessionStorage.setItem(this.announcementsPopupSeenKey, 'true');
  }

  private hasSeenAnnouncementsNudge(): boolean {
    if (!this.announcementsNudgeSeenKey) return false;
    return sessionStorage.getItem(this.announcementsNudgeSeenKey) === 'true';
  }

  private markAnnouncementsNudgeSeen(): void {
    if (!this.announcementsNudgeSeenKey) return;
    sessionStorage.setItem(this.announcementsNudgeSeenKey, 'true');
  }

  get showAnnouncementsPopup(): boolean {
    return (
      this.showBottomNav &&
      !this.loading &&
      !this.hasError &&
      !this.notificationsDrawerOpen &&
      this.unreadAnnouncementsCount > 0 &&
      !this.hasSeenAnnouncementsPopup()
    );
  }

  // Show only on mobile-ish UI (your app’s mobile layout flag), only when unread exists,
  // only when drawer is not open, and only until the user has seen/dismissed it once per session.
  get showAnnouncementsNudge(): boolean {
    return (
      this.showBottomNav &&
      !this.notificationsDrawerOpen &&
      this.unreadAnnouncementsCount > 0 &&
      !this.hasSeenAnnouncementsNudge()
    );
  }

  openAnnouncementsFromPopup(): void {
    this.markAnnouncementsPopupSeen();
    this.markAnnouncementsNudgeSeen();
    this.onAnnouncementsIconClick();
  }

  dismissAnnouncementsPopup(): void {
    this.markAnnouncementsPopupSeen();
    this.cdr.markForCheck();
  }

  openAnnouncementsFromNudge(): void {
    this.markAnnouncementsPopupSeen();
    this.markAnnouncementsNudgeSeen();
    this.onAnnouncementsIconClick();
  }

  dismissAnnouncementsNudge(): void {
    this.markAnnouncementsPopupSeen();
    this.markAnnouncementsNudgeSeen();
    this.cdr.markForCheck();
  }

  private hasSeenDashboardPulse(): boolean {
    if (!this.dashboardPulseSeenKey) return false;
    return sessionStorage.getItem(this.dashboardPulseSeenKey) === 'true';
  }

  private markDashboardPulseSeen(): void {
    // Only persist "seen" state in emergency mode
    if (this.appMode !== 'emergency') return;

    if (!this.dashboardPulseSeenKey) return;
    sessionStorage.setItem(this.dashboardPulseSeenKey, 'true');
  }

  private hasSeenDashboardToast(): boolean {
    if (!this.dashboardToastSeenKey) return false;
    return sessionStorage.getItem(this.dashboardToastSeenKey) === 'true';
  }

  private markDashboardToastSeen(): void {
    // Only persist "seen" state in emergency mode
    if (this.appMode !== 'emergency') return;

    if (!this.dashboardToastSeenKey) return;
    sessionStorage.setItem(this.dashboardToastSeenKey, 'true');
  }

  startDashboardAttentionIfNeeded(): void {
    if (
      this.appMode !== 'emergency' ||
      !this.showBottomNav ||
      this.loading ||
      this.hasError ||
      this.mobileActiveTab !== 'home'
    ) {
      return;
    }

    if (this.hasSeenDashboardPulse()) return;

    this.markDashboardPulseSeen();
    this.showDashboardAttention = true;
    this.cdr.markForCheck();

    clearTimeout(this.dashboardAttentionTimeoutId);
    this.dashboardAttentionTimeoutId = setTimeout(() => {
      this.showDashboardAttention = false;
      this.cdr.markForCheck();
    }, 12000); // 10–15s target (set to 12s)
  }

  stopDashboardAttention(): void {
    this.showDashboardAttention = false;
    clearTimeout(this.dashboardAttentionTimeoutId);
    this.cdr.markForCheck();
  }

  showDashboardToastOnce(): void {
    if (
      this.appMode !== 'emergency' ||
      !this.showBottomNav ||
      this.loading ||
      this.hasError ||
      this.mobileActiveTab !== 'home'
    ) {
      return;
    }

    if (this.hasSeenDashboardToast()) return;

    this.markDashboardToastSeen();
    this.showDashboardToast = true;
    this.cdr.markForCheck();

    clearTimeout(this.dashboardToastTimeoutId);
    this.dashboardToastTimeoutId = setTimeout(() => {
      this.showDashboardToast = false;
      this.cdr.markForCheck();
    }, 8000);
  }

  dismissDashboardToast(markSeen = false): void {
    this.showDashboardToast = false;
    clearTimeout(this.dashboardToastTimeoutId);

    if (markSeen) {
      this.markDashboardToastSeen();
    }

    this.cdr.markForCheck();
  }

  get showInfoTabHint(): boolean {
    if (!this.emergencyNudgesEnabled) return false;

    // Only emergency mode, mobile, Home tab, and until dismissed
    return this.appMode === 'emergency' &&
      this.showBottomNav &&
      this.mobileActiveTab === 'home' &&
      !this.infoTabHintDismissed;
  }

  dismissInfoTabHint(): void {
    this.infoTabHintDismissed = true;
    if (this.infoTabHintKey) {
      localStorage.setItem(this.infoTabHintKey, 'true');
    }
  }

  get isEmergencyMode(): boolean {
    return this.appMode === 'emergency';
  }

  onLandingLogoLoad(ev: Event) {
    const img = ev.target as HTMLImageElement;
    const w = img.naturalWidth || 1;
    const h = img.naturalHeight || 1;
    const r = w / h;

    this.isLogoTall = r < 0.90;
    this.isLogoWide = r > 1.35;
  }

  onEmergencyLogoLoad(ev: Event) {
    const img = ev.target as HTMLImageElement;
    const w = img.naturalWidth || 1;
    const h = img.naturalHeight || 1;
    const r = w / h;

    this.isEmergencyLogoTall = r < 0.90;
    this.isEmergencyLogoWide = r > 1.35;
  }

  isNonEmpty(value?: string | null): boolean {
    return !!(value && value.trim().length > 0);
  }
}
